import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useWebSocketStore, MESSAGE_TYPES } from "../../stores/websocketStore";

import { withStyles } from "@mui/styles";
import classNames from "classnames";

import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import MotionFade from "../common/MotionFade";
import MapCard, { maps } from "./MapCard";
import { toast } from "../../universal/toast";
import { notifyIfCannotSendFriendRequest } from "../../utils/friendRequests";

import ShareIcon from "@mui/icons-material/Share";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import MapIcon from "@mui/icons-material/Map";
import TuneIcon from "@mui/icons-material/Tune";
import ConfirmDialog from "../common/ConfirmDialog";
import JoinLobbyDialog from "./JoinLobbyDialog";
import CircularProgress from "@mui/material/CircularProgress";
import AcceptMatchDialog from "./AcceptMatchDialog";
import TimerFromTimestamp from "./TimerFromTimestamp";
import LobbyPlayerBox from "./LobbyPlayerBox";
import FriendSlot from "./FriendSlot";
import LobbyParticles from "./LobbyParticles";
import SystemStatusBar from "./SystemStatusBar";
import FriendListSideBar from "./FriendListSideBar";
import LobbySettingsMenu from "./LobbySettingsMenu";
import LobbyQueueModeSelector from "./LobbyQueueModeSelector";
import InviteDialog from "./InviteDialog";
import { LobbyAccountStatusNotice } from "./lobbyAccountStatusNotice";
import V2DesignPrompt from "./V2DesignPrompt";
import BetaWelcomeCard from "./BetaWelcomeCard";
import LobbyMatchNightBanner from "./LobbyMatchNightBanner";
import OuroCodeDialog from "./OuroCodeDialog";
import SmallerMatchIntroDialog from "./SmallerMatchIntroDialog";
import useSmallerMatchIntro from "./useSmallerMatchIntro";
import { getSmallerMatchOptedInCopy } from "./smallerMatchIntro";
import {
  shouldShowOuroCode,
  recordOuroCodeAck,
} from "./ouroCode";
import EmptyQueueHint from "./EmptyQueueHint";
import QueueEstimateIndicator from "./QueueEstimateIndicator";
import useQueueEstimate from "./useQueueEstimate";
import { TIERS } from "../../constants/membership";
import {
  canClearAllLocations,
  canClearAllMaps,
  canToggleLocationSelection,
  canToggleMapSelection,
} from "../../utils/mapSelectionLimits";
import {
  resolveLobbyLocationsToDisplay,
  resolveLobbyMapsToDisplay,
  resolveSelectionOwnerTier,
} from "./lobbyEffectiveSelections";
import GroupIcon from "@mui/icons-material/Group";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PublicIcon from "@mui/icons-material/Public";
import LoginIcon from "@mui/icons-material/Login";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { transform } from "lodash";
import utils from "../../universal/utils";
import { fetchMembershipStatus } from "../../redux/actions/catalogActions";
import { handleLobbyPlayerLeftMessage } from "./lobbyRealtimeMessageHandlers";
import {
  getMatchServerReadyMatchId,
  isAlreadyOnMatchRoute,
} from "./lobbyPageUtils";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "../public-site/discordInviteUrl";
import { buildReferralInviteLink } from "../../universal/referralLinks";
import { getMapMetaById } from "../../constants/mapMeta";
import {
  SCROLLBAR_THUMB_COLOR,
  SCROLLBAR_THUMB_HOVER_COLOR,
  SCROLLBAR_TRACK_COLOR,
} from "../../universal/scrollbarStyles";
import {
  primeMatchNotificationSounds,
  waitForMatchNotificationSoundsReady,
  playMatchFoundSound,
  playMatchAcceptSound,
  playSmallerMatchOfferSoundOnce,
  clearSmallerMatchOfferSound,
  armMatchPageLandedSoundForMatch,
} from "../../universal/matchNotificationSounds";
const { fetchJsonWithDiagnostics } = utils;
const CUSTOM_MAP_SUBSCRIPTION_CONFIRMATIONS_KEY =
  "ouro.customMapSubscriptionConfirmations.v1";

function normalizeMapSelectionToMapId(mapName) {
  return resolveMatchMapIdFromSelectionKey(mapName);
}

function normalizeLocationId(locationId) {
  if (locationId === null || locationId === undefined) {
    return "";
  }
  return String(locationId).trim();
}

function buildWorkshopItemUrl(workshopItemId) {
  const normalized = String(workshopItemId || "").trim();
  if (!normalized) return null;
  return `https://steamcommunity.com/sharedfiles/filedetails/?id=${encodeURIComponent(
    normalized,
  )}`;
}

function readConfirmedCustomMapWorkshopIds() {
  if (typeof window === "undefined" || !window.localStorage) {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(
      CUSTOM_MAP_SUBSCRIPTION_CONFIRMATIONS_KEY,
    );
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    );
  } catch (_err) {
    return new Set();
  }
}

function writeConfirmedCustomMapWorkshopIds(workshopIds) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    const deduped = [...new Set((workshopIds || []).filter(Boolean))]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .sort();
    window.localStorage.setItem(
      CUSTOM_MAP_SUBSCRIPTION_CONFIRMATIONS_KEY,
      JSON.stringify(deduped),
    );
  } catch (_err) {
    // localStorage might be blocked by browser/privacy settings.
  }
}

export const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: "100vh",
    overflowX: "hidden",
    [theme.breakpoints.up("xl")]: {
      justifyContent: "center",
      minHeight: "calc(100vh - 8rem)",
    },
  },
  homepageRoot: {
    width: "100%",
    minHeight: "100vh",
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
  },
  alertContainer: {
    position: "relative",
    zIndex: 10,
    width: "100%",
    margin: "0 auto",
    paddingTop: "0.75rem",
    [theme.breakpoints.up("xl")]: {
      paddingTop: "1rem",
    },
  },
  lobbyFrame: {
    width: "100%",
    margin: "12px auto",
    textAlign: "center",
    position: "relative",
    flex: 1,
    transition: "transform 0.3s ease-in-out, margin-right 0.3s ease-in-out",
    backgroundColor: "transparent",
    boxSizing: "border-box",
    borderRadius: "12px",
    overflow: "hidden",
  },
  lobbyContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    width: "100%",
    margin: "0 auto",
    borderRadius: "8px",
    boxSizing: "border-box",
    background: `
      linear-gradient(180deg, 
        rgba(28, 30, 32, 0.96) 0%, 
        rgba(20, 22, 25, 0.98) 100%
      )
    `,
    border: "1px solid rgba(255, 255, 255, 0.04)",
    overflow: "hidden",
    padding: "4.5rem 1.5rem 1.5rem",
    gap: "2rem",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage:
        "repeating-linear-gradient(0deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 3px)",
      zIndex: 0,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(232, 163, 61, 0.35) 50%, transparent 100%)",
      pointerEvents: "none",
      zIndex: 1,
    },
    "& > *": {
      position: "relative",
      zIndex: 2,
    },
  },
  cornerBracket: {
    position: "absolute",
    width: "14px",
    height: "14px",
    borderColor: "rgba(232, 163, 61, 0.55)",
    pointerEvents: "none",
    zIndex: 3,
  },
  cornerBracketTL: {
    top: "8px",
    left: "8px",
    borderTop: "1px solid",
    borderLeft: "1px solid",
  },
  cornerBracketTR: {
    top: "8px",
    right: "8px",
    borderTop: "1px solid",
    borderRight: "1px solid",
  },
  cornerBracketBL: {
    bottom: "8px",
    left: "8px",
    borderBottom: "1px solid",
    borderLeft: "1px solid",
  },
  cornerBracketBR: {
    bottom: "8px",
    right: "8px",
    borderBottom: "1px solid",
    borderRight: "1px solid",
  },
  lobbySection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.85rem",
    width: "100%",
  },
  sectionLabel: {
    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
    fontSize: "0.6875rem",
    fontWeight: 500,
    letterSpacing: "0.16em",
    color: "rgba(232, 163, 61, 0.7)",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    gap: "0.55rem",
    "&::after": {
      content: '""',
      flex: 1,
      maxWidth: "120px",
      height: "1px",
      background:
        "linear-gradient(90deg, rgba(232, 163, 61, 0.25) 0%, transparent 100%)",
    },
    "&::before": {
      content: '""',
      flex: 1,
      maxWidth: "120px",
      height: "1px",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(232, 163, 61, 0.25) 100%)",
    },
  },
  lobbyBackground: {
    position: "absolute",
    width: "100%",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "transparent",
    zIndex: 0,
  },
  containerLobbySettings: {
    position: "absolute",
    top: "0.6rem",
    left: "0.6rem",
    [theme.breakpoints.up("xl")]: {
      top: "1rem",
      left: "1rem",
    },
  },
  referralBanner: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    padding: "0.4rem 0.85rem",
    borderRadius: "999px",
    backgroundColor: "rgba(28, 30, 32, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    flexShrink: 0,
    marginBottom: "-0.5rem",
  },
  containerFriendlistButton: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    width: "100%",
    padding: "1rem 0",
  },
  containerActionsNoInLobby: {
    position: "absolute",
    top: "0.6rem",
    right: "0.6rem",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    [theme.breakpoints.up("xl")]: {
      top: "1rem",
      right: "1rem",
      gap: "0.8rem",
    },
  },
  lobbyActionIconButton: {
    width: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)",
    height: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)",
    backgroundColor: "rgba(28, 30, 32, 0.85)",
    backdropFilter: "blur(8px)",
    transition: "all 0.15s ease-in-out",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 1)",
      transform: "scale(1.05)",
      boxShadow:
        "0 0 12px rgba(232, 163, 61, 0.35), 0 0 4px rgba(232, 163, 61, 0.25)",
      borderColor: "rgba(232, 163, 61, 0.3)",
    },
    [theme.breakpoints.up("xl")]: {
      width: "3.1rem",
      height: "3.1rem",
    },
  },
  lobbyActionIcon: {
    fontSize: "clamp(1.05rem, 0.95rem + 0.35vw, 1.3rem)",
    color: "#ffffff",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.45rem",
    },
  },
  enterWithCodeButton: {
    padding:
      "clamp(0.35rem, 0.25rem + 0.5vw, 0.7rem) clamp(0.7rem, 0.5rem + 0.6vw, 1.4rem)",
    backgroundColor: "rgba(28, 30, 32, 0.7)",
    textTransform: "uppercase",
    backdropFilter: "blur(8px)",
    transition: "all 0.3s ease-in-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "clamp(2rem, 1.5rem + 1vw, 3rem)",
    zIndex: 11,
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 1)",
      boxShadow:
        "0 0 12px rgba(232, 163, 61, 0.35), 0 0 4px rgba(232, 163, 61, 0.25)",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0.9rem 1.5rem",
      height: "3rem",
    },
  },
  // enterWithCodeIcon: {
  //   fontSize: "clamp(0.8rem, 0.45rem + 0.5vw, 1rem)",
  //   color: "#ffffff",
  //   lineHeight: 1,
  //   marginRight: "0.35rem",
  //   [theme.breakpoints.up("xl")]: {
  //     fontSize: "1.2rem",
  //   },
  // },
  enterWithCodeText: {
    fontSize: "clamp(0.8rem, 0.45rem + 0.5vw, 1rem)",
    color: "#ffffff",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.2rem",
    },
  },
  friendlistTooltip: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "14px",
      padding: "0.4rem 0.6rem",
    },
  },
  lobbySettingsButton: {
    padding:
      "clamp(0.35rem, 0.25rem + 0.5vw, 0.7rem) clamp(0.7rem, 0.5rem + 0.6vw, 1.4rem)",
    backgroundColor: "rgba(28, 30, 32, 0.7)",
    textTransform: "uppercase",
    backdropFilter: "blur(8px)",
    transition: "all 0.3s ease-in-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "clamp(2rem, 1.5rem + 1vw, 3rem)",
    zIndex: 11,
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 1)",
      boxShadow:
        "0 0 12px rgba(232, 163, 61, 0.35), 0 0 4px rgba(232, 163, 61, 0.25)",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0.9rem 1.5rem",
      height: "3rem",
    },
  },
  lobbySettingsButtonDisabled: {
    backgroundColor: "rgba(28, 30, 32, 0.5)",
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 0.5)",
    },
  },
  friendlistButton: {
    // Match top-right action icon buttons sizing
    width: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)",
    height: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)",
    minWidth: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)",
    padding: 0,
    borderRadius: "50%",
    backgroundColor: "rgba(28, 30, 32, 0.85)",
    textTransform: "uppercase",
    backdropFilter: "blur(8px)",
    transition: "all 0.15s ease-in-out",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 11,
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 1)",
      boxShadow:
        "0 0 12px rgba(232, 163, 61, 0.35), 0 0 4px rgba(232, 163, 61, 0.25)",
      borderColor: "rgba(232, 163, 61, 0.3)",
    },
    [theme.breakpoints.up("xl")]: {
      width: "3.1rem",
      height: "3.1rem",
      minWidth: "3.1rem",
    },
  },
  friendlistIcon: {
    fontSize: "clamp(1.05rem, 0.95rem + 0.35vw, 1.3rem)",
    color: "#ffffff",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.45rem",
    },
  },
  onlineIndicator: {
    position: "absolute",
    bottom: "0.1rem",
    right: "-0.1rem",
    fontSize: "0.6rem",
    color: "#666666",
    backgroundColor: "rgba(28, 30, 32, 0.9)",
    borderRadius: "50%",
    padding: "0.1rem",
    zIndex: 1,
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.8rem",
      bottom: "0.1rem",
      right: "-0.2rem",
    },
  },
  onlineIndicatorGreen: {
    color: "#38b000",
  },
  settingsIcon: {
    fontSize: "1rem",
    color: "#ffffff",
    marginRight: "0.35rem",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.25rem",
    },
  },
  settingsText: {
    fontSize: "clamp(0.8rem, 0.45rem + 0.5vw, 1rem)",
    color: "#ffffff",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.2rem",
    },
  },
  containerLobbyCodeFriendButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "absolute",
    top: "0.6rem",
    right: "0.6rem",
    zIndex: 11,
    [theme.breakpoints.up("xl")]: {
      top: "1rem",
      right: "1rem",
    },
  },
  containerLobbyCode: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
  },
  lobbyCodeLayout: {
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    justifyContent: "flex-end",
  },
  lobbyCodeTitle: {
    fontSize: "clamp(0.7rem, 0.6rem + 0.3vw, 1rem)",
    fontWeight: 525,
    color: "#cecece",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.1rem",
    },
  },
  lobbyCodeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "0.5rem",
  },
  lobbyCodeText: {
    fontSize: "clamp(0.8rem, 0.7rem + 0.3vw, 1.1rem)",
    color: "#ffffff",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.3rem",
    },
  },
  copyTooltip: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "14px",
      padding: "0.4rem 0.6rem",
    },
  },
  copyTooltipArrow: {
    color: "#1C1E20",
    [theme.breakpoints.up("xl")]: {
      fontSize: "14px",
    },
  },
  copyButton: {
    width: "clamp(1.1rem, 1rem + 0.3vw, 1.5rem)",
    height: "clamp(1.1rem, 1rem + 0.3vw, 1.5rem)",
    padding: 0,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.12)",
    backgroundColor: "#1C1E20",
    transition: "all 0.3s ease",
    color: "#e0e0e0",
    "&:hover": {
      backgroundColor: "#232629",
    },
    [theme.breakpoints.up("xl")]: {
      width: "1.7rem",
      height: "1.7rem",
    },
  },
  copyIcon: {
    fontSize: "clamp(0.6rem, 0.5rem + 0.2vw, 0.9rem)",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.1rem",
    },
  },
  exitLobbyButton: {
    padding:
      "clamp(0.35rem, 0.25rem + 0.5vw, 0.7rem) clamp(0.7rem, 0.5rem + 0.6vw, 1.4rem)",
    height: "clamp(2rem, 1.5rem + 1vw, 3rem)",
    color: "#ffffff",
    backgroundColor: "#A52A2A",
    transition: "all 0.3s ease-in-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#8B0000",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0.9rem 1.5rem",
      fontSize: "1.2rem",
      height: "3rem",
    },
  },
  exitLobbyButtonDisabled: {
    backgroundColor: "#A52A2A70",
    "&:hover": {
      backgroundColor: "#A52A2A70",
    },
  },
  queueButtonCont: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "0.7rem",
    zIndex: 10,
    width: "auto",
    [theme.breakpoints.up("xl")]: {
      gap: "0.9rem",
    },
  },
  containerSearchingLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "1rem",
    animation:
      "$searchingSlideIn 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
    opacity: 0,
    transform: "translateY(20px) scale(0.95)",
  },
  searchingTimerLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "0.5rem 0.85rem",
    backgroundColor: "rgba(28, 30, 32, 1)",
    borderRadius: "6px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
    zIndex: 11,
    [theme.breakpoints.up("xl")]: {
      padding: "0.6rem 1rem",
      gap: "0.6rem",
    },
  },
  timerText: {
    color: "#ffffff",
    fontSize: "0.95rem",
    fontWeight: 700,
    fontFamily: "monospace",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.2rem",
    },
  },
  searchingMatchLoader: {
    width: "20px !important",
    height: "20px !important",
    [theme.breakpoints.up("xl")]: {
      width: "24px !important",
      height: "24px !important",
    },
  },
  queueHintText: {
    color: "rgba(255, 255, 255, 0.45)",
    fontSize: "0.72rem",
    fontWeight: 400,
    marginTop: "0.15rem",
    fontStyle: "italic",
    maxWidth: "260px",
    textAlign: "center",
    lineHeight: 1.4,
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.82rem",
      maxWidth: "300px",
    },
  },
  teamSlotsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "14px",
    maxWidth: "1040px",
    width: "100%",
    margin: "0 auto",
    zIndex: 10,
    padding: "0 1rem",
    boxSizing: "border-box",
    [theme.breakpoints.up("xl")]: {
      maxWidth: "1200px",
      gap: "18px",
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  containerMapSelection: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: "2rem 0",
    margin: "0 auto",
    background: "#1C1E20",
  },
  mapSelectionContent: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    maxWidth: "60rem",
    padding: "0 2rem",
    zIndex: 3,
    boxSizing: "border-box",
  },
  mapSelectionTitle: {
    width: "100%",
    textAlign: "center",
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "1rem",
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
    letterSpacing: "0.5px",
  },
  mapContainer: {
    display: "flex",
    flexDirection: "row",
    padding: "1rem 0",
    gap: "1.2rem",
    width: "100%",
    overflowX: "hidden",
    flexWrap: "wrap",
    justifyContent: "center",
    scrollbarWidth: "thin",
    scrollbarColor: "#E8A33D rgba(232, 163, 61, 0.1)",
    "&::-webkit-scrollbar": {
      height: "4px",
      "-webkit-appearance": "none",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(232, 163, 61, 0.1)",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(90deg, #E8A33D, #E55A00)",
      borderRadius: "2px",
      boxShadow: "0 0 4px rgba(232, 163, 61, 0.3)",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "linear-gradient(90deg, #E8A33D, #B97A1F)",
    },
  },
  containerMapSelectedRecap: {
    display: "flex",
    flexDirection: "row",
    gap: 0,
    marginTop: "2rem",
    minHeight: "12rem",
    background: `
      linear-gradient(135deg, 
        rgba(28, 30, 32, 0.9) 0%, 
        rgba(42, 45, 49, 0.95) 50%, 
        rgba(28, 30, 32, 1) 100%
      )
    `,
    borderRadius: 16,
    border: "1px solid rgba(232, 163, 61, 0.2)",
    boxShadow:
      "0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    backdropFilter: "blur(10px)",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 20%, rgba(232, 163, 61, 0.05) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(232, 163, 61, 0.03) 0%, transparent 50%)
      `,
      pointerEvents: "none",
      zIndex: 1,
    },
  },
  mapPreviewSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: "12rem",
    width: "100%",
    position: "relative",
    zIndex: 2,
    borderRadius: 12,
    overflow: "visible",
    border: "2px solid rgba(232, 163, 61, 0.3)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
  },
  mapPreviewCard: {
    width: "30%",
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    position: "relative",
    borderRadius: "12px 0 0 12px",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        "linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.4) 100%)",
      zIndex: 1,
    },
  },
  mapPreviewContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "1rem 1.5rem",
    borderRadius: "0 12px 12px 0",
    backgroundColor: "rgba(28, 30, 32, 0.95)",
    position: "relative",
    zIndex: 5,
  },
  mapPreviewHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  mapPreviewName: {
    color: "#ffffff",
    fontSize: "1.2rem",
    fontWeight: 700,
    textAlign: "left",
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.7)",
    letterSpacing: "1px",
    margin: 0,
  },
  selectedMapsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "0.8rem",
    marginTop: "0.2rem",
    position: "relative",
  },
  selectedMapsTitle: {
    color: "#8C8F8A",
    textAlign: "left",
    fontSize: "0.85rem",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
  },
  selectedMapsList: {
    display: "flex",
    flexWrap: "wrap",
    position: "relative",
    gap: "0.5rem",
    marginTop: "-0.4rem",
  },
  mapLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    backgroundColor: "rgba(232, 163, 61, 0.2)",
    border: "1px solid rgba(232, 163, 61, 0.4)",
    borderRadius: 20,
    padding: "0.25rem 0.8rem",
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0 2px 8px rgba(232, 163, 61, 0.2)",
    position: "relative",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.3)",
      borderColor: "rgba(232, 163, 61, 0.6)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(232, 163, 61, 0.3)",
    },
  },
  mapLabelText: {
    fontSize: "0.85rem",
    color: "#ffffff",
  },
  mapLabelRemove: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.9)",
      borderColor: "rgba(232, 163, 61, 0.9)",
      transform: "scale(1.1)",
      boxShadow: "0 2px 8px rgba(232, 163, 61, 0.4)",
    },
  },
  mapLabelRemoveIcon: {
    fontSize: "0.8rem",
    color: "#ffffff",
  },
  mapPreviewActions: {
    display: "flex",
    alignItems: "center",
  },
  mapCounter: {
    color: "#8C8F8A",
    fontSize: "0.85rem",
    fontWeight: 500,
    textAlign: "left",
    textShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
    letterSpacing: "0.3px",
    margin: 0,
  },
  containerNoMapSelected: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
    textAlign: "center",
    padding: "3rem 2rem",
  },
  noSelectedMapsTitle: {
    color: "#8C8F8A",
    fontSize: "1.2rem",
    fontWeight: 600,
    textAlign: "center",
    marginBottom: "1rem",
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
    letterSpacing: "0.5px",
  },
  noSelectedMapsDescription: {
    color: "#8C8F8A",
    fontSize: "1rem",
    textAlign: "center",
    marginBottom: "1rem",
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
    letterSpacing: "0.5px",
  },
  queueButton: {
    margin: "0 auto",
    fontSize: "24pt",
    padding: "4px 16px",
  },
  anim: {
    position: "absolute",
    top: 13,
    right: -35,
  },
  lookformatch: {
    marginTop: "0.2rem",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.25rem",
    },
  },
  matchmakingOptionsCont: {
    border: "solid 1px #b5b1b3",
    borderRadius: 8,
    margin: "35px auto",
    position: "relative",
    textAlign: "left",
    paddingLeft: 10,
    paddingBottom: 10,
    paddingTop: 10,
    minHeight: 200,
    width: 740,
  },
  matchmakingSectionTitle: {
    position: "absolute",
    top: -23,
    fontWeight: 525,
  },
  mmOptionRowCb: {
    height: 30,
  },
  minHoursPlayedField: {
    width: 100,
    fontSize: 11,
  },
  vspacer10: {
    height: 10,
  },
  hoursPlayedNote: {
    position: "relative",
    left: 5,
    color: "#cecece",
    fontSize: 12,
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "100vh",
    backgroundColor: "transparent",
    zIndex: 999,
    cursor: "default",
    opacity: 0,
    transition: "opacity 0.3s ease-in-out",
    pointerEvents: "none",
  },
  backdropVisible: {
    opacity: 1,
    pointerEvents: "auto",
  },
  containerSideBar: {
    position: "fixed",
    top: "4rem",
    bottom: 0,
    right: 0,
    width: "calc(100vw - 3rem)",
    maxWidth: "16rem",
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    transform: "translateX(100%)",
    backgroundColor: "#1C1E20",
    borderLeft: "1px solid rgb(70, 70, 70)",
    padding: "0.5rem 0.4rem",
    zIndex: 1000,
    transition: "transform 0.3s ease-in-out",
    [theme.breakpoints.up("sm")]: {
      width: "16rem",
      maxWidth: "16rem",
    },
    [theme.breakpoints.up("md")]: {
      width: "16.5rem",
      maxWidth: "16.5rem",
    },
    [theme.breakpoints.up("lg")]: {
      width: "17rem",
      maxWidth: "17rem",
    },
    [theme.breakpoints.up("xl")]: {
      width: "17.5rem",
      maxWidth: "17.5rem",
      top: "4.7rem",
      padding: "0.55rem 0.45rem",
    },
  },
  containerSideBarOpen: {
    transform: "translateX(0)",
  },
  containerLobbySettingsMenu: {
    position: "fixed",
    top: "4rem",
    bottom: 0,
    left: 0,
    width: "100%",
    maxWidth: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    transform: "translateX(-100%)",
    backgroundColor: "#1C1E20",
    borderRight: "1px solid rgb(70, 70, 70)",
    padding: "1rem",
    zIndex: 1000,
    transition: "transform 0.3s ease-in-out",
    [theme.breakpoints.up("sm")]: {
      width: "17rem",
      maxWidth: "17rem",
    },
    [theme.breakpoints.up("md")]: {
      width: "17.8rem",
      maxWidth: "17.8rem",
    },
    [theme.breakpoints.up("lg")]: {
      width: "19rem",
      maxWidth: "19rem",
    },
    [theme.breakpoints.up("xl")]: {
      width: "20.5rem",
      maxWidth: "20.5rem",
      top: "4.7rem",
      padding: "0.65rem 0.55rem",
    },
  },
  containerLobbySettingsMenuOpen: {
    transform: "translateX(0)",
  },
  settingsTooltip: {
    backgroundColor: "#1C1E20",
    padding: "0.5rem 0.6rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "14px",
      padding: "0.6rem 0.65rem",
    },
  },
  blockSettingsPanel: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
    pointerEvents: "none",
  },
  lobbySettingsDialogPaper: {
    width: "min(96vw, 980px)",
    maxWidth: "980px",
    maxHeight: "88vh",
    margin: "0.75rem",
    backgroundColor: "#1C1E20",
    border: "1px solid rgba(232, 163, 61, 0.22)",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.45)",
    [theme.breakpoints.up("xl")]: {
      width: "min(88vw, 1080px)",
      maxWidth: "1080px",
      maxHeight: "90vh",
    },
  },
  lobbySettingsDialogBody: {
    position: "relative",
    width: "100%",
    maxHeight: "88vh",
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: `${SCROLLBAR_THUMB_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: SCROLLBAR_TRACK_COLOR,
      borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: SCROLLBAR_THUMB_COLOR,
      border: "2px solid transparent",
      backgroundClip: "padding-box",
      borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: SCROLLBAR_THUMB_HOVER_COLOR,
    },
  },
  lobbyNotifyPlayer: {
    position: "fixed",
    bottom: "1rem",
    right: 0,
    zIndex: 10000,
    borderRadius: "8px 0 0 8px",
    padding: "1rem 2rem",
    boxShadow: "none",
    border: "none",
    minWidth: "300px",
    maxWidth: "100%",
    animation: "$playerJoinedSlideIn 0.5s ease-out",
    "&.slideOut": {
      animation: "$playerJoinedSlideOut 0.5s ease-in forwards",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "1.2rem 2.1rem",
      minWidth: "350px",
    },
  },
  joined: {
    backgroundColor: "#4caf50",
    boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
    border: "1px solid rgba(76, 175, 80, 0.5)",
  },
  left: {
    backgroundColor: "#A52A2A",
    boxShadow: "0 4px 20px rgba(165, 42, 42, 0.3)",
    border: "1px solid rgba(165, 42, 42, 0.5)",
  },
  playerNotificationText: {
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.2rem",
    },
  },
  inviteLinkBtn: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  playButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 36px",
    backgroundColor: "#E8A33D",
    color: "#0B0D0F",
    border: "none",
    borderRadius: 4,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "15px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "background 120ms ease",
    position: "relative",
    overflow: "hidden",
    zIndex: 11,
    "&:hover": {
      backgroundColor: "#F4B752",
    },
    "&:active": {
      transform: "translateY(1px)",
    },
    "&:disabled": {
      backgroundColor: "#333",
      color: "rgba(255,255,255,0.3)",
      cursor: "not-allowed",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "16px 42px",
      fontSize: "17px",
    },
  },
  playButtonDisabled: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 36px",
    backgroundColor: "#1a1c1e",
    color: "rgba(255, 255, 255, 0.3)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 4,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "15px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "not-allowed",
    position: "relative",
    overflow: "hidden",
    opacity: 0.5,
    zIndex: 11,
    [theme.breakpoints.up("xl")]: {
      padding: "16px 42px",
      fontSize: "17px",
    },
  },
  stopSearchButton: {
    backgroundColor: "#A52A2A",
    background: "linear-gradient(135deg, #A52A2A 0%, #8B0000 100%)",
    color: "#ffffff",
    fontSize: "0.98rem",
    fontWeight: 700,
    padding: "0.58rem 1.85rem",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    textTransform: "uppercase",
    letterSpacing: "1px",
    boxShadow:
      "0 6px 20px rgba(165, 42, 42, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      transition: "left 0.5s ease-in-out",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #B52A2A 0%, #9B0000 100%)",
      boxShadow:
        "0 8px 25px rgba(165, 42, 42, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
      transform: "translateY(-2px)",
      "&::before": {
        left: "100%",
      },
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 4px 15px rgba(165, 42, 42, 0.4)",
    },
    "&:disabled": {
      background: "linear-gradient(135deg, #666 0%, #555 100%)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
      cursor: "not-allowed",
      transform: "none",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0.72rem 2.15rem",
      fontSize: "1.05rem",
    },
  },
  smallerMatchOfferBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.35rem",
    marginTop: "0.75rem",
    maxWidth: "320px",
    padding: "0.7rem 0.85rem",
    backgroundColor: "rgba(28, 30, 32, 1)",
    borderRadius: "6px",
    border: "1px solid rgba(232, 163, 61, 0.35)",
  },
  smallerMatchOfferTitle: {
    margin: 0,
    fontSize: "0.72rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#E8A33D",
    textAlign: "center",
    lineHeight: 1.35,
  },
  smallerMatchOfferHint: {
    margin: 0,
    fontSize: "0.68rem",
    fontWeight: 500,
    lineHeight: 1.4,
    color: "rgba(255, 255, 255, 0.55)",
    textAlign: "center",
  },
  smallerMatchOfferActions: {
    display: "flex",
    justifyContent: "center",
    gap: "0.5rem",
    marginTop: "0.35rem",
    flexWrap: "wrap",
  },
  smallerMatchPlayButton: {
    backgroundColor: "rgba(232, 163, 61, 0.16)",
    color: "#E8A33D",
    border: "1px solid rgba(232, 163, 61, 0.42)",
    fontSize: "0.66rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "0.42rem 0.85rem",
    minWidth: 0,
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.22)",
    },
  },
  smallerMatchDeclineButton: {
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.7)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "0.42rem 0.85rem",
    minWidth: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
    },
  },
  bottomBarsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.6rem",
    zIndex: 5,
    width: "auto",
    pointerEvents: "none",
  },
  settingsRecapContainer: {
    position: "relative",
    padding: "0.5rem 0.75rem",
    backgroundColor: "rgba(28, 30, 32, 0.8)",
    borderRadius: "10px",
    border: "1px solid rgba(232, 163, 61, 0.2)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)",
    boxSizing: "border-box",
    maxWidth: "calc(100% - 1rem)",
    display: "inline-flex",
    flexWrap: "nowrap",
    justifyContent: "center",
    gap: "0.5rem",
    alignItems: "center",
    cursor: "pointer",
    pointerEvents: "auto",
    fontFamily: "'JetBrains Mono', monospace",
    transition:
      "border-color 0.15s ease-in-out, background-color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "rgba(28, 30, 32, 0.95)",
      borderColor: "rgba(232, 163, 61, 0.45)",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0.6rem 0.85rem",
      gap: "0.6rem",
    },
  },
  recapRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.35rem 0.75rem",
    borderRadius: "6px",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    transition: "border-color 0.15s ease-in-out",
    "&:hover": {
      borderColor: "rgba(232, 163, 61, 0.35)",
    },
  },
  recapIcon: {
    display: "block",
    fontSize: "1rem",
    color: "#E8A33D",
    flexShrink: 0,
    lineHeight: 0,
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.1rem",
    },
  },
  recapText: {
    fontSize: "0.8rem",
    color: "#ffffff",
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "none",
    lineHeight: 1,
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.9rem",
    },
  },
  recapEmptyText: {
    fontSize: "0.74rem",
    color: "rgba(255, 255, 255, 0.45)",
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "none",
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.85rem",
    },
  },
  unavailableActionMessage: {
    position: "absolute",
    top: "20%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10000,
    background: "linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)",
    padding: "0.4rem 1.5rem",
    borderRadius: "6px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.up("xl")]: {
      top: "24%",
      padding: "0.6rem 1.8rem",
    },
  },
  unavailableActionMessageText: {
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: 500,
    textAlign: "center",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.2rem",
    },
  },
  matchmakingStatusBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "0 24px",
    backgroundColor: "#1C1E20",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
    zIndex: 50,
  },
  matchmakingStatusTime: {
    color: "#E8A33D",
  },
  matchmakingStatusLink: {
    color: "#E8A33D",
    textDecoration: "none",
    "&:hover": {
      color: "#F4B752",
    },
  },
  matchmakingStatusDivider: {
    color: "rgba(255,255,255,0.25)",
  },
  "@keyframes backgroundFloat": {
    "0%, 100%": {
      transform: "translateZ(-150px) translateY(0px)",
    },
    "50%": {
      transform: "translateZ(-150px) translateY(-10px)",
    },
  },
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-6px)" },
  },
  "@keyframes searchingSlideIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px) scale(0.95)",
    },
    "60%": {
      opacity: 0.8,
      transform: "translateY(-5px) scale(1.02)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },
  "@keyframes scaleInBounce": {
    "0%": {
      transform: "scale(0.5)",
      opacity: 0,
    },
    "25%": {
      transform: "scale(1.1)",
      opacity: 0.6,
    },
    "50%": {
      transform: "scale(0.95)",
      opacity: 0.8,
    },
    "75%": {
      transform: "scale(1.02)",
      opacity: 0.9,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
  "@keyframes playerJoinedSlideIn": {
    "0%": {
      transform: "translateX(100%)",
      opacity: 0,
    },
    "100%": {
      transform: "translateX(0)",
      opacity: 1,
    },
  },
  "@keyframes playerJoinedSlideOut": {
    "0%": {
      transform: "translateX(0)",
      opacity: 1,
    },
    "100%": {
      transform: "translateX(100%)",
      opacity: 0,
    },
  },
  "@media (min-width: 1024px)": {
    homepageRoot: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minHeight: "min(100vh - 6.5rem, 50rem)",
      margin: "0 auto",
      boxSizing: "border-box",
      // overflow: 'hidden',
      [theme.breakpoints.up("sm")]: {
        width: "70%",
        maxWidth: "70%",
      },
      [theme.breakpoints.up("md")]: {
        width: "80%",
        maxWidth: "80%",
      },
      [theme.breakpoints.up("lg")]: {
        width: "70%",
        maxWidth: "70%",
      },
      [theme.breakpoints.up("2200")]: {
        minHeight: "62rem",
      },
    },
    lobbyFrame: {
      width: "100%",
      margin: "12px auto",
      textAlign: "center",
      position: "relative",
      flex: 1,
      transition: "transform 0.3s ease-in-out, margin-right 0.3s ease-in-out",
      backgroundColor: "transparent",
      boxSizing: "border-box",
      borderRadius: "12px",
      overflow: "hidden",
    },
  },
});

// WebSocket connection is now managed by the external store

import { AVAILABLE_MAPS, AVAILABLE_WINGMAN_MAPS, formatMapName, resolveMatchMapIdFromSelectionKey } from "../../constants/maps";

const FOUNDER_SPOTS_DISMISSED_KEY = "ouro_founder_spots_dismissed";

function isFounderSpotsDismissed() {
  try {
    return localStorage.getItem(FOUNDER_SPOTS_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function LobbyPage({ classes, actions, catalog }) {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [founderSpotsDismissed, setFounderSpotsDismissed] = useState(isFounderSpotsDismissed);
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [settingsResetVersion, setSettingsResetVersion] = useState(0);
  const { sendOwnerAction, subscribe, clientId } = useWebSocketStore();
  const friendsRef = useRef();
  const playedMatchSoundRef = useRef(null);
  const matchAcceptDialogStateRef = useRef(null);
  const matchAcceptanceCountRef = useRef(new Map());
  const suppressNextOuroCodePromptRef = useRef(false);
  const pendingOuroCodeModeRef = useRef(null);
  const ouroCodeAcceptLockRef = useRef(false);
  const pendingQueueModeRef = useRef(null);
  const [soloQueueMode, setSoloQueueMode] = useState("standard");

  // Function to mask the invite code for display (streamer-friendly)
  const maskInviteCode = (code) => {
    if (!code || code.length < 5) return code;
    const firstThree = code.substring(0, 3);
    const lastTwo = code.substring(code.length - 2);
    const middleLength = code.length - 5;
    return `${firstThree}${"•".repeat(middleLength)}${lastTwo}`;
  };

  const history = useHistory();
  const basePath = utils.getEdgeBasePath();
  const [queueClosedInfo, setQueueClosedInfo] = useState({
    queueClosed: false,
    updatedAtMs: null,
    schedule: null,
    error: null,
  });
  const queueClosedStopRef = useRef(false);
  const [discordInviteUrl, setDiscordInviteUrl] = useState(
    DEFAULT_DISCORD_INVITE_URL,
  );
  const [scheduleCountdown, setScheduleCountdown] = useState("");
  const [founderProgress, setFounderProgress] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [customMapSubscriptionDialog, setCustomMapSubscriptionDialog] =
    useState({
      open: false,
      maps: [],
    });
  const [pendingQueueStartMode, setPendingQueueStartMode] = useState(null);
  const [ouroCodeDialogOpen, setOuroCodeDialogOpen] = useState(false);
  const [pendingOuroCodeMode, setPendingOuroCodeMode] = useState(null);

  // Pre-download all match notification sounds as soon as the Lobby mounts
  // so they are ready before any match-found dialog or redirect fires.
  useEffect(() => {
    primeMatchNotificationSounds();
    waitForMatchNotificationSoundsReady().catch(() => {
      // Non-fatal: if preload fails, play helpers still lazy-load on demand.
    });
  }, []);

  useEffect(() => {
    if (catalog?.user?.userId) {
      dispatch(fetchMembershipStatus(catalog.user.userId));
    }
  }, [catalog?.user?.userId, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const countryParam = user?.country
      ? `?country=${encodeURIComponent(user.country)}`
      : "";

    const loadQueueStatus = async () => {
      try {
        const result = await fetchJsonWithDiagnostics(
          `${basePath}/api/v1/matchmaking/queue/status${countryParam}`,
          {
            credentials: "include",
          },
          {
            context: "Lobby queue status",
            extra: {
              route: history.location?.pathname || null,
              userId: user?.userId || null,
              steamId: resolvedUserSteamId,
            },
          },
        );
        const data = result.data || {};
        if (!result.ok || data?.ok === false) {
          throw new Error(
            result.error ||
              data?.err ||
              data?.message ||
              "Failed to load queue status",
          );
        }
        if (cancelled) return;
        if (process.env.NODE_ENV !== 'production') console.log("[LobbyPage] Queue status payload", {
          queueClosed: data.queueClosed,
          schedule: data.schedule || null,
          country: user?.country || null,
        });
        setQueueClosedInfo({
          queueClosed: data.queueClosed === true,
          updatedAtMs:
            typeof data.updatedAtMs === "number" ? data.updatedAtMs : null,
          schedule: data.schedule || null,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setQueueClosedInfo((prev) => ({
          ...prev,
          error: e?.message || String(e),
        }));
      }
    };

    loadQueueStatus();
    const pid = setInterval(loadQueueStatus, 10000);
    return () => {
      cancelled = true;
      clearInterval(pid);
    };
  }, [
    basePath,
    history.location?.pathname,
    resolvedUserSteamId,
    user?.userId,
    user?.country,
  ]);

  const fetchMatchStatus = async (matchId) => {
    if (!matchId) return { ok: false, status: null, isTerminal: false };
    try {
      const controller = new AbortController();
      const timeoutPid = setTimeout(() => controller.abort(), 3000);

      const result = await fetchJsonWithDiagnostics(
        `${basePath}/api/gc/public/matchPage/match/${matchId}`,
        { signal: controller.signal },
        {
          context: "Lobby pre-navigation match status",
          extra: {
            route: history.location?.pathname || null,
            matchId,
            userId: user?.userId || null,
            steamId: resolvedUserSteamId,
          },
        },
      );
      clearTimeout(timeoutPid);

      const data = result.data || {};
      const status = data?.match?.status || null;
      const isTerminal = status === "FINISHED" || status === "CANCELLED";
      return {
        ok: result.ok && Boolean(data?.ok),
        status,
        isTerminal,
        error: result.error || null,
      };
    } catch (e) {
      return {
        ok: false,
        status: null,
        isTerminal: false,
        error: e?.message || String(e),
      };
    }
  };

  const {
    user,
    lobbyMapsSelected = [],
    lobbyWingmanMapsSelected = [],
    selectedServerLocations = [],
    steamData = {},
    matchmakingOptions,
    lobbyState,
    matchAcceptDialogState,
    friendRequestError,
    friendRequestSuccess,
    friendRequests = [],
    pendingRequests = [],
    friends = [],
    friendToDelete = null,
    inviteData = null,
    inviteCountdown = 60000,
    inviteDialogOpen = false,
    inviteState = {},
    currentLobby = null,
    currentLobbyCode = null,
    playerJoinedNotification = { visible: false, nickname: null, type: null },
    playerKickedNotification = { visible: false, message: null },
    showUnavailableActionMessage = false,
    queueErrorMessage = null,
    queueBlockedModal = {
      open: false,
      reason: null,
      message: null,
      blockedMembers: [],
    },
    matchmakingStarted = false,
    lobbyCodeNotFound = false,
    lobbyFull = false,
    queueStatus = null,
  } = catalog;
  const resolvedUserSteamId =
    user?.steamId || user?.steamId64 || user?.steam64Id || null;
  const { open: smallerMatchIntroOpen, dismiss: dismissSmallerMatchIntro } =
    useSmallerMatchIntro({
      blocked:
        matchmakingStarted ||
        lobbyState === "searching" ||
        Boolean(matchAcceptDialogState?.matchId) ||
        ouroCodeDialogOpen ||
        Boolean(queueBlockedModal.open) ||
        Boolean(customMapSubscriptionDialog.open),
    });

  const isQueueEffectivelyClosed =
    queueClosedInfo.queueClosed ||
    (queueClosedInfo.schedule?.enabled &&
      !queueClosedInfo.schedule?.currentlyOpen);

  useEffect(() => {
    if (
      isQueueEffectivelyClosed &&
      lobbyState === "searching" &&
      !queueClosedStopRef.current
    ) {
      queueClosedStopRef.current = true;
      try {
        handleStopSearch();
      } catch (e) {
        // ignore
      }
    }
    if (!isQueueEffectivelyClosed) {
      queueClosedStopRef.current = false;
    }
  }, [isQueueEffectivelyClosed, lobbyState]);

  // Fetch Discord invite URL
  useEffect(() => {
    fetchPublicDiscordInviteUrl(basePath)
      .then((url) => setDiscordInviteUrl(url))
      .catch(() => {});
  }, [basePath]);

  useEffect(() => {
    fetch(`${basePath}/api/v1/founder/progress`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok) setFounderProgress(data);
      })
      .catch(() => {});
  }, [basePath]);

  useEffect(() => {
    if (!user?.userId) return;
    fetch(`${basePath}/api/v1/referral/my-code`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && (data?.referralCode || data?.code))
          setReferralCode(data.referralCode || data.code);
      })
      .catch(() => {});
  }, [basePath, user?.userId]);

  // Live countdown to schedule open time
  useEffect(() => {
    const schedule = queueClosedInfo.schedule;
    if (
      !schedule?.enabled ||
      schedule?.currentlyOpen ||
      schedule?.opensAtUTC == null
    ) {
      setScheduleCountdown("");
      return;
    }

    const computeCountdown = () => {
      const now = new Date();
      const openTarget = new Date(now);
      openTarget.setUTCHours(schedule.opensAtUTC, 0, 0, 0);
      if (openTarget <= now) openTarget.setDate(openTarget.getDate() + 1);

      const diffMs = openTarget - now;
      const totalMin = Math.max(0, Math.floor(diffMs / 60000));
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    setScheduleCountdown(computeCountdown());
    const tid = setInterval(
      () => setScheduleCountdown(computeCountdown()),
      60000,
    );
    return () => clearInterval(tid);
  }, [queueClosedInfo.schedule]);

  // Auto-generate invite code for existing lobbies that don't have one
  useEffect(() => {
    if (currentLobbyCode && !inviteCode && !inviteLoading) {
      handleGenerateInviteCode();
    }
  }, [currentLobbyCode]);

  const { hoursPlayed } = steamData;

  const currentLobbyData =
    currentLobbyCode && currentLobby ? currentLobby : null;
  const isLeader = user ? currentLobbyData?.hostId === user.userId : false;
  const smallerMatchOffer = queueStatus?.smallerMatchOffer || null;
  const smallerMatchTeamSize = Number(smallerMatchOffer?.teamSize);
  const smallerMatchLabel = Number.isInteger(smallerMatchTeamSize)
    ? `${smallerMatchTeamSize}v${smallerMatchTeamSize}`
    : "";
  const smallerMatchStatus = smallerMatchOffer?.status || null;
  const showSmallerMatchOffer =
    Boolean(smallerMatchOffer) &&
    smallerMatchStatus !== "declined" &&
    Boolean(smallerMatchLabel);
  const canActOnSmallerMatch = Boolean(isLeader || !currentLobbyCode);

  useEffect(() => {
    if (showSmallerMatchOffer && smallerMatchStatus === "offered") {
      playSmallerMatchOfferSoundOnce(smallerMatchOffer);
      return undefined;
    }
    if (!smallerMatchOffer) {
      clearSmallerMatchOfferSound();
    }
    return undefined;
  }, [showSmallerMatchOffer, smallerMatchStatus, smallerMatchTeamSize, smallerMatchOffer]);
  const lobbyMembers =
    currentLobbyData && Array.isArray(currentLobbyData.members)
      ? currentLobbyData.members
      : [];
  const selfMember = user
    ? lobbyMembers.find((m) => m.userId === user.userId)
    : null;
  const lobbyQueueMode = currentLobby
    ? (currentLobby.queueMode === "wingman" ? "wingman" : "standard")
    : soloQueueMode;
  const isWingmanQueue = lobbyQueueMode === "wingman";
  const otherMembers = user
    ? lobbyMembers.filter((m) => m.userId !== user.userId).slice(0, isWingmanQueue ? 1 : 4)
    : [];

  const isLobbyFull =
    currentLobbyData?.members?.length >= currentLobbyData?.maxPlayers || false;
  const canEditLobbySettings = user
    ? currentLobbyData?.hostId === user.userId
    : false;
  const lobbyOptionsToDisplay = !currentLobbyCode
    ? matchmakingOptions
    : currentLobbyData?.lobbyOptions || {};
  const selectionOwnerTier = resolveSelectionOwnerTier({
    currentLobbyCode,
    currentLobbyData,
    viewerTier: catalog?.membership?.tier || TIERS.FREE,
  });
  const enabledServerLocationIds = Array.isArray(catalog.serverLocations)
    ? catalog.serverLocations
        .map((location) => normalizeLocationId(location.id))
        .filter(Boolean)
    : [];
  const lobbyMapsToDisplay = resolveLobbyMapsToDisplay({
    currentLobbyCode,
    personalMaps: isWingmanQueue ? lobbyWingmanMapsSelected : lobbyMapsSelected,
    lobbyMaps: isWingmanQueue
      ? currentLobbyData?.lobbyWingmanMapsSelected
      : currentLobbyData?.lobbyMapsSelected,
    selectionOwnerTier,
    queueMode: lobbyQueueMode,
  });
  const lobbyServerLocationsToDisplay = resolveLobbyLocationsToDisplay({
    currentLobbyCode,
    personalLocations: selectedServerLocations,
    lobbyLocations: currentLobbyData?.lobbyServerLocationsSelected,
    enabledLocationIds: enabledServerLocationIds,
    selectionOwnerTier,
  });
  const hasSelectedMaps = (lobbyMapsToDisplay?.length || 0) > 0;
  const hasSelectedServerLocations =
    (lobbyServerLocationsToDisplay?.length || 0) > 0;
  const hasRequiredQueueSelections =
    hasSelectedMaps && hasSelectedServerLocations;
  const partyMemberUserIds = currentLobbyCode
    ? lobbyMembers.map((m) => m.userId).filter(Boolean)
    : [];
  const queueEstimate = useQueueEstimate({
    basePath,
    enabled: Boolean(user?.userId && hasRequiredQueueSelections),
    selectedMaps: lobbyMapsToDisplay,
    selectedServerLocations: lobbyServerLocationsToDisplay,
    matchmakingOptions: lobbyOptionsToDisplay,
    country: selfMember?.country || user?.country || null,
    age: selfMember?.age || user?.age || null,
    gender: selfMember?.gender || user?.gender || null,
    elo: isWingmanQueue ? (user?.eloWingman ?? 1000) : (user?.elo || null),
    queueMode: lobbyQueueMode,
    partyMemberUserIds,
  });
  const MAX_RECAP_ITEMS = 2;
  const summarizeRecapItems = (
    items,
    {
      maxItems = MAX_RECAP_ITEMS,
      total = null,
      allLabel = null,
      emptyLabel = "None",
    } = {},
  ) => {
    const safeItems = [...new Set((items || []).filter(Boolean))];
    if (safeItems.length === 0) {
      return { text: emptyLabel, isEmpty: true };
    }
    if (
      allLabel &&
      typeof total === "number" &&
      total > 0 &&
      safeItems.length >= total
    ) {
      return { text: allLabel, isEmpty: false };
    }
    if (safeItems.length <= maxItems) {
      return { text: safeItems.join(", "), isEmpty: false };
    }
    const visible = safeItems.slice(0, maxItems).join(", ");
    return {
      text: `${visible} +${safeItems.length - maxItems}`,
      isEmpty: false,
    };
  };
  const mapDisplayNames = lobbyMapsToDisplay
    .map((map) => formatMapName(map))
    .filter(Boolean);
  const knownMapNames = (isWingmanQueue ? AVAILABLE_WINGMAN_MAPS : AVAILABLE_MAPS).map((map) => formatMapName(map));
  const uniqueMapDisplayNames = [...new Set(mapDisplayNames)];
  const mapsRecapSummary = summarizeRecapItems(uniqueMapDisplayNames, {
    total: knownMapNames.length,
    allLabel: "All maps",
    emptyLabel: "No maps",
  });
  const selectedCustomWorkshopMaps = lobbyMapsToDisplay.reduce(
    (acc, selectedMap) => {
      const mapId = normalizeMapSelectionToMapId(selectedMap);
      if (!mapId) return acc;

      const mapMeta = getMapMetaById(mapId);
      const workshopItemId = String(mapMeta?.workshopItemId || "").trim();
      if (!workshopItemId) return acc;
      if (acc.some((item) => item.workshopItemId === workshopItemId)) {
        return acc;
      }

      const mapDisplayName =
        mapMeta?.display || formatMapName(selectedMap || mapId);
      const workshopUrl = buildWorkshopItemUrl(workshopItemId);
      acc.push({
        mapId,
        mapDisplayName,
        workshopItemId,
        workshopUrl,
      });
      return acc;
    },
    [],
  );
  const activeMatchmakingOptionLabels = [];
  if (lobbyOptionsToDisplay.above_hours) {
    activeMatchmakingOptionLabels.push(
      `${lobbyOptionsToDisplay.above_hours_figure || 100}h+`,
    );
  }
  if (lobbyOptionsToDisplay.total_steam_cs_matches_played) {
    activeMatchmakingOptionLabels.push(
      `Valve ${lobbyOptionsToDisplay.total_steam_cs_matches_played_figure || 10}+`,
    );
  }
  if (lobbyOptionsToDisplay.total_faceit_matches_played) {
    activeMatchmakingOptionLabels.push(
      `FaceIT ${
        lobbyOptionsToDisplay.total_faceit_matches_played_figure || 10
      }+`,
    );
  }
  if (lobbyOptionsToDisplay.maxEloDifferenceInTeam) {
    activeMatchmakingOptionLabels.push(
      `ELO gap ${lobbyOptionsToDisplay.maxEloDifferenceInTeam_figure || 150}`,
    );
  }
  if (lobbyOptionsToDisplay.min_player_age) {
    activeMatchmakingOptionLabels.push(
      `Age ${lobbyOptionsToDisplay.min_player_age_figure || 16}+`,
    );
  }
  if (lobbyOptionsToDisplay.player_has_inventory) {
    activeMatchmakingOptionLabels.push("Inventory");
  }
  if (lobbyOptionsToDisplay.player_has_expensive_inventory) {
    activeMatchmakingOptionLabels.push("Inv >$500");
  }
  if (lobbyOptionsToDisplay.premium_or_pro_only) {
    activeMatchmakingOptionLabels.push("Premium/Pro");
  }
  if (lobbyOptionsToDisplay.biometric_verified_only) {
    activeMatchmakingOptionLabels.push("Biometric");
  }
  if (
    user?.gender === "female" &&
    lobbyOptionsToDisplay.prefer_female_teammates_if_possible
  ) {
    activeMatchmakingOptionLabels.push("Prefer women");
  }
  if (
    lobbyOptionsToDisplay.play_with_countries &&
    Array.isArray(lobbyOptionsToDisplay.play_with_countries_list) &&
    lobbyOptionsToDisplay.play_with_countries_list.length > 0
  ) {
    const countryCount = lobbyOptionsToDisplay.play_with_countries_list.length;
    activeMatchmakingOptionLabels.push(
      `${countryCount} countr${countryCount === 1 ? "y" : "ies"}`,
    );
  }
  const matchmakingRecapSummary = summarizeRecapItems(
    activeMatchmakingOptionLabels,
    { maxItems: 3, emptyLabel: "No filters" },
  );

  const serverLocationsRecapSummary = (() => {
    const allLocations = Array.isArray(catalog.serverLocations)
      ? catalog.serverLocations
      : [];
    const selectedNames = lobbyServerLocationsToDisplay
      .map((id) => {
        const loc = allLocations.find((l) => l.id === id);
        return loc ? loc.displayName || loc.name || loc.id : null;
      })
      .filter(Boolean);
    return summarizeRecapItems(selectedNames, {
      total: allLocations.length,
      allLabel: "All regions",
      emptyLabel: "No regions",
    });
  })();

  const shouldBlockActions = () => {
    if (lobbyState === "searching" && !currentLobbyCode) return true;

    if (lobbyState === "searching" && currentLobbyCode && isLeader) return true;

    if (matchmakingStarted && !isLeader && currentLobbyCode) return true;

    return false;
  };

  const canFlipCards = () => {
    if (lobbyState === "searching" && currentLobbyCode && isLeader)
      return false;

    if (matchmakingStarted && !isLeader && currentLobbyCode) return false;

    return true;
  };

  const pendingBySlot = React.useMemo(() => {
    const map = catalog.inviteSlotAssignments || {};
    const result = {};
    Object.entries(map).forEach(([guestId, slotIndex]) => {
      const friend = (friends || []).find(
        (f) => f.friendData && String(f.friendData.userId) === String(guestId),
      );
      if (friend) {
        result[slotIndex] = {
          nickname: friend.friendData.nickname,
          picture: friend.friendData.picture,
        };
      }
    });
    return result;
  }, [catalog.inviteSlotAssignments, friends]);

  useEffect(() => {
    if (user) {
      actions.getMapsSelectionFromServer(user);
      if (resolvedUserSteamId) {
        // One-shot initial fetches — ongoing updates arrive via Socket.IO push
        actions.initFriendRequests(resolvedUserSteamId);
        actions.getPendingRequests(resolvedUserSteamId);
        actions.getFriends({
          userId: user.userId,
          steamId: resolvedUserSteamId,
        });
      } else {
        console.warn(
          "[LobbyPage] Skipping social init until a Steam ID is available",
          {
            userId: user?.userId || null,
            steamId: user?.steamId || null,
            steamId64: user?.steamId64 || null,
            steam64Id: user?.steam64Id || null,
          },
        );
      }
      actions.getLobbyByUserId(user.userId);

      friendsRef.current = catalog.friends;
    }

    return () => {};
  }, [user, resolvedUserSteamId]);

  // Reconcile social state when the tab returns to foreground after being hidden
  useEffect(() => {
    if (!user || !resolvedUserSteamId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        actions.getFriends({ userId: user.userId, steamId: resolvedUserSteamId });
        actions.initFriendRequests(resolvedUserSteamId);
        actions.getPendingRequests(resolvedUserSteamId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resolvedUserSteamId]);

  useEffect(() => {
    actions.clearMatchData();
    actions.fetchServerLocations();
  }, []);

  // Subscribe to WebSocket messages
  useEffect(() => {
    const unsubscribe = subscribe(({ message }) => {
      if (!message) return;

      const eventAsJson = message;
      if (process.env.NODE_ENV !== 'production') console.log("Received WebSocket message:", eventAsJson);

      // Handle different message types using discriminated unions
      switch (eventAsJson.messageId) {
        case MESSAGE_TYPES.FRIEND_REQUEST_ACCEPTED:
          if (process.env.NODE_ENV !== 'production') console.log("friend request accepted", eventAsJson);

          const newFriend = {
            userA: eventAsJson.userA,
            userB: eventAsJson.userB,
            friendSteamId: eventAsJson.friendSteamId,
            isUserA: eventAsJson.userA === resolvedUserSteamId,
            friendData: {
              _id: eventAsJson.friendData._id,
              userId: eventAsJson.friendData.userId,
              picture: eventAsJson.friendData.picture,
              elo: eventAsJson.friendData.elo,
              nickname: eventAsJson.friendData.nickname,
              isOnline: eventAsJson.friendData.isOnline,
            },
          };

          actions.friendRequestAccepted({ newFriend });
          break;

        case MESSAGE_TYPES.FRIEND_STATUS_UPDATE:
          if (process.env.NODE_ENV !== 'production') console.log("Updating friend status:", eventAsJson);
          actions.updateFriendStatus({
            friendSteamId: eventAsJson.friendSteamId,
            isOnline: eventAsJson.isOnline,
            playerStatus: eventAsJson.playerStatus,
            lastOnlineUpdate: eventAsJson.lastOnlineUpdate,
          });
          break;

        case MESSAGE_TYPES.LOBBY_MEMBER_STATUS_UPDATE:
          if (process.env.NODE_ENV !== 'production') console.log("Updating lobby member status:", eventAsJson);
          actions.updateLobbyMemberStatus({
            userId: eventAsJson.userId,
            isOnline: eventAsJson.isOnline,
            playerStatus: eventAsJson.playerStatus,
            lastOnlineUpdate: eventAsJson.lastOnlineUpdate,
          });
          break;

        case MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION:
          if (process.env.NODE_ENV !== 'production') console.log(
            `🎮 [MATCH_FOUND] Received match-found-notification for matchId: ${eventAsJson.matchId}, timeoutSeconds: ${eventAsJson.timeoutSeconds}, userId: ${user?.userId}`,
          );
          actions.showMatchFoundNotification(
            eventAsJson.matchId,
            eventAsJson.timeoutSeconds,
            eventAsJson.map,
            eventAsJson.totalPlayers,
            {
              expiresAt: eventAsJson.expiresAt,
              hasAccepted: eventAsJson.hasAccepted,
              acceptedCount: eventAsJson.acceptedCount,
              allAccepted: eventAsJson.allAccepted,
              slots: eventAsJson.slots,
              partyAcceptedUserIds: eventAsJson.partyAcceptedUserIds,
              location: eventAsJson.location || null,
              mapVotePending: eventAsJson.mapVotePending,
              teams: eventAsJson.teams,
            }
          );
          actions.clearQueueStatus();
          break;

        case MESSAGE_TYPES.MATCH_ACCEPTANCE_UPDATE:
          if (process.env.NODE_ENV !== 'production') console.log(
            `🎮 [MATCH_ACCEPTANCE] Received acceptance update: ${eventAsJson.acceptedCount}/${eventAsJson.totalPlayers} for matchId: ${eventAsJson.matchId}, userId: ${user?.userId}`,
          );
          try {
            const matchId = eventAsJson.matchId;
            const nextAcceptedCount = Number(eventAsJson.acceptedCount ?? 0);
            const prevAcceptedCount =
              matchAcceptanceCountRef.current.get(matchId) ?? 0;

            // Always track latest count (prevents replay when the UI mounts mid-flow)
            matchAcceptanceCountRef.current.set(matchId, nextAcceptedCount);

            const activeMatchId = matchAcceptDialogStateRef.current?.matchId;
            const isForActiveMatch = Boolean(
              activeMatchId && activeMatchId === matchId,
            );

            // Play a tick sound whenever *someone* accepts (count increases)
            if (isForActiveMatch && nextAcceptedCount > prevAcceptedCount) {
              playMatchAcceptSound();
            }
          } catch (e) {
            // Never let sound issues break the handler
          }
          actions.updateMatchAcceptanceStatus({
            matchId: eventAsJson.matchId,
            acceptedCount: eventAsJson.acceptedCount,
            totalPlayers: eventAsJson.totalPlayers,
            allAccepted: eventAsJson.allAccepted,
            mapVotePending: eventAsJson.mapVotePending,
            map: eventAsJson.map,
          });
          break;

        case MESSAGE_TYPES.INVITE_RECEIVED:
          const friend = friendsRef.current.find(
            (f) => f.friendSteamId === eventAsJson.fromUserSteamId,
          );

          if (friend) {
            const inviteDataObj = {
              inviteId: eventAsJson.inviteId,
              fromUserId: eventAsJson.fromUserId,
              fromUserSteamId: eventAsJson.fromUserSteamId,
              fromUserName: friend.friendData.nickname,
              fromUserPicture: friend.friendData.picture,
              fromUserElo: friend.friendData.elo,
              timestamp: eventAsJson.timestamp,
              expiresIn: eventAsJson.expiresIn,
            };
            actions.startInvite({ inviteData: inviteDataObj });
          } else {
            console.error(
              "Friend not found for invite from:",
              eventAsJson.fromUserSteamId,
            );
          }
          break;

        case MESSAGE_TYPES.INVITE_EXPIRED:
          actions.inviteExpired(eventAsJson.toUserId);
          break;

        case MESSAGE_TYPES.INVITE_DECLINED:
          actions.inviteDeclined(eventAsJson.toUserId);
          break;

        case MESSAGE_TYPES.LOBBY_CREATED:
          actions.createLobby(eventAsJson.lobby);
          actions.closeJoinLobbyDialog();
          toast.success("Lobby created", { durationMs: 4000 });

          // If invite code is auto-generated (solo lobby), store it
          if (eventAsJson.inviteCode) {
            setInviteCode(eventAsJson.inviteCode);
            setInviteExpiresAt(eventAsJson.inviteCodeExpiresAt);
          } else if (eventAsJson.lobby?.lobbyCode) {
            // If no invite code but lobby exists, generate one
            handleGenerateInviteCode();
          }
          break;

        case MESSAGE_TYPES.INVITE_ACCEPTED:
          // Join toast is emitted once from LOBBY_ADDED_PLAYER —
          // do not toast here or invite accepts double-fire.
          actions.inviteAccepted(eventAsJson.guestId);
          break;

        case MESSAGE_TYPES.LOBBY_PLAYER_LEFT:
          handleLobbyPlayerLeftMessage({
            event: eventAsJson,
            user,
            actions,
            friends: friendsRef.current || [],
            clearInviteCodeState() {
              setInviteCode(null);
              setInviteExpiresAt(null);
            },
          });
          break;

        case MESSAGE_TYPES.LOBBY_PLAYER_REMOVED_SILENT:
          actions.lobbyPlayerLeft(
            eventAsJson.lobbyCode,
            eventAsJson.removedUserId,
            false,
            null,
            eventAsJson.totalPlayers,
          );
          break;

        case MESSAGE_TYPES.LOBBY_ADDED_PLAYER:
          actions.updateLobbyWithNewPlayer(eventAsJson.lobby);
          break;

        case MESSAGE_TYPES.LOBBY_OPTIONS_UPDATED:
          actions.lobbyLeaderUpdatedOptions({
            lobbyCode: eventAsJson.lobbyCode,
            lobbyOptions: eventAsJson.lobbyOptions,
          });
          break;

        case MESSAGE_TYPES.LOBBY_MAPS_UPDATED:
          actions.lobbyLeaderUpdatedMaps({
            lobbyCode: eventAsJson.lobbyCode,
            lobbyMapsSelected: eventAsJson.lobbyMapsSelected,
            lobbyWingmanMapsSelected: eventAsJson.lobbyWingmanMapsSelected,
          });
          break;

        case MESSAGE_TYPES.LOBBY_QUEUE_MODE_UPDATED:
          actions.lobbyLeaderUpdatedQueueMode({
            lobbyCode: eventAsJson.lobbyCode,
            queueMode: eventAsJson.queueMode,
            maxPlayers: eventAsJson.maxPlayers,
            lobbyWingmanMapsSelected: eventAsJson.lobbyWingmanMapsSelected,
          });
          break;

        case MESSAGE_TYPES.LOBBY_SERVER_LOCATIONS_UPDATED:
          actions.lobbyLeaderUpdatedServerLocations({
            lobbyCode: eventAsJson.lobbyCode,
            lobbyServerLocationsSelected:
              eventAsJson.lobbyServerLocationsSelected,
          });
          break;

        case MESSAGE_TYPES.LOBBY_SETTINGS_DENIED:
          setSettingsResetVersion((value) => value + 1);
          if (eventAsJson.attemptedMessageId === "lobby-queue-mode-update") {
            pendingQueueModeRef.current = null;
            setSoloQueueMode("standard");
            actions.lobbyQueueModeSwitched({
              lobbyCode: eventAsJson.lobbyCode,
              queueMode: "standard",
              maxPlayers: 5,
            });
          }
          actions.showQueueErrorMessage(
            eventAsJson.message
              || (eventAsJson.reason === "tier-locked"
                ? "Your current plan cannot edit these shared lobby settings."
                : eventAsJson.reason === "invalid-selection"
                  ? "That lobby setting change is not valid."
                  : eventAsJson.reason === "searching"
                    ? "Stop searching before switching queue mode."
                    : "Only the lobby leader can change settings."),
          );
          break;

        case MESSAGE_TYPES.INVITE_ERROR:
          actions.showQueueErrorMessage(
            eventAsJson.message ||
              eventAsJson.error ||
              "Unable to send or accept the lobby invite.",
          );
          break;

        case MESSAGE_TYPES.INVITE_REJECTED_FULL:
          actions.showQueueErrorMessage(
            "The invite could not be accepted because your lobby is full.",
          );
          break;

        case MESSAGE_TYPES.LOBBY_PLAYER_KICKED:
          const message = eventAsJson.messageKick;
          const kickedId = eventAsJson.kickedPlayerId;
          if (user && kickedId === user.userId) {
            actions.leaveLobby();
            actions.notifyPlayerHasBeenKicked(message);
            // Clear invite code state when kicked from lobby
            setInviteCode(null);
            setInviteExpiresAt(null);
          } else if (kickedId) {
            actions.lobbyPlayerLeft(eventAsJson.lobbyCode, kickedId);
            actions.notifyPlayerHasBeenKicked(message);
          }
          break;

        case MESSAGE_TYPES.FRIEND_REQUEST_RECEIVED:
          if (process.env.NODE_ENV !== 'production') console.log("Friend request received via WebSocket:", eventAsJson);
          actions.addFriendRequestLocally(
            eventAsJson.fromUserSteamId,
            eventAsJson.toUserSteamId,
          );
          actions.initFriendRequests(resolvedUserSteamId);
          break;

        case MESSAGE_TYPES.FRIEND_REQUEST_ACCEPTED_NOTIFICATION:
          if (process.env.NODE_ENV !== 'production') console.log("Friend request accepted notification:", eventAsJson);
          if (eventAsJson.friendSteamId) {
            actions.removePendingRequestLocally(eventAsJson.friendSteamId);
          }
          actions.getFriends({
            userId: user.userId,
            steamId: resolvedUserSteamId,
          });
          actions.getPendingRequests(resolvedUserSteamId);

          if (catalog.currentLobby) {
            actions.getCurrentLobby(catalog.currentLobby.lobbyCode);
          }
          break;

        case MESSAGE_TYPES.FRIEND_REQUEST_DECLINED_NOTIFICATION:
          if (process.env.NODE_ENV !== 'production') console.log("Friend request declined notification:", eventAsJson);
          if (eventAsJson.toUserSteamId) {
            actions.removePendingRequestLocally(eventAsJson.toUserSteamId);
          }
          actions.getPendingRequests(resolvedUserSteamId);
          break;

        case MESSAGE_TYPES.FRIEND_DELETED_NOTIFICATION:
          if (eventAsJson.deletedBySteamId) {
            actions.removeFriendLocally(eventAsJson.deletedBySteamId);
            actions.removePendingRequestLocally(eventAsJson.deletedBySteamId);
          }
          break;

        case MESSAGE_TYPES.FRIENDS_BOOTSTRAP:
          actions.setFriendsFromBootstrap(eventAsJson.friends);
          break;

        case MESSAGE_TYPES.FRIEND_REQUESTS_BOOTSTRAP:
          actions.setFriendRequestsFromBootstrap(eventAsJson.friendRequests);
          break;

        case MESSAGE_TYPES.FRIENDS_LIST:
          actions.setFriendsFromBootstrap(eventAsJson.friends);
          break;

        case MESSAGE_TYPES.FRIEND_REQUEST_ERROR:
          if (process.env.NODE_ENV !== 'production') console.log("Friend request error via WebSocket:", eventAsJson);
          actions.sendFriendRequestError(eventAsJson.error);

          if (
            eventAsJson.toUserSteamId &&
            !eventAsJson.error.includes("already exists")
          ) {
            actions.removePendingRequestLocally(eventAsJson.toUserSteamId);
          }
          break;

        case MESSAGE_TYPES.MATCHMAKING_STARTED_NOTIFICATION:
          if (process.env.NODE_ENV !== 'production') console.log("Matchmaking started by leader");
          actions.notifyMatchmakingStarted();
          break;

        case MESSAGE_TYPES.MATCHMAKING_STOPPED_NOTIFICATION:
          if (process.env.NODE_ENV !== 'production') console.log("Matchmaking stopped by leader");
          actions.resetMatchmakingStarted();
          actions.clearQueueStatus();
          break;

        case MESSAGE_TYPES.QUEUE_STATUS_UPDATE:
          actions.updateQueueStatus({
            playersInQueue: eventAsJson.playersInQueue,
            playersNeeded: eventAsJson.playersNeeded,
            algorithmEnabled: eventAsJson.algorithmEnabled,
            hint: eventAsJson.hint,
            estimatedWaitSeconds: eventAsJson.estimatedWaitSeconds,
            smallerMatchOffer: eventAsJson.smallerMatchOffer,
          });
          break;

        case MESSAGE_TYPES.LOBBY_CODE_NOT_FOUND:
          actions.lobbyCodeNotFound();
          break;

        case MESSAGE_TYPES.LOBBY_FULL:
          actions.lobbyFull();
          break;

        case MESSAGE_TYPES.JOINED_LOBBY_BY_CODE:
          actions.closeJoinLobbyDialog();
          break;

        case MESSAGE_TYPES.SOLO_LOBBY_ERROR:
          if (pendingQueueModeRef.current === "wingman") {
            pendingQueueModeRef.current = null;
            setSoloQueueMode("standard");
          }
          console.error("Error creating solo lobby:", eventAsJson.error);
          actions.showUnavailableActionMessage();
          break;

        // Handshake messages are handled by websocketStore, ignore here
        case MESSAGE_TYPES.INIT_HANDSHAKE:
        case MESSAGE_TYPES.HANDSHAKE_COMPLETED:
          break;

        case MESSAGE_TYPES.MATCH_SERVER_READY: {
          const newMatchId = getMatchServerReadyMatchId(eventAsJson);
          actions.lobbyStopSearch({ userId: user.userId });
          actions.closeMatchAcceptDialog();

          if (
            isAlreadyOnMatchRoute(history.location?.pathname, newMatchId)
          ) {
            break;
          }

          actions.clearMatchData();

          if (newMatchId) {
            if (process.env.NODE_ENV !== 'production') console.log(
              `🎮 [MATCH_SERVER_READY] Received for matchId=${newMatchId} (currentPath=${history.location?.pathname || "unknown"})`,
              eventAsJson,
            );

            // Guard: on reconnect, the server may emit an old match id.
            // If the match is already FINISHED/CANCELLED, stay on Lobby so user can re-queue.
            fetchMatchStatus(newMatchId).then((statusCheck) => {
              if (statusCheck.isTerminal) {
                console.warn(
                  `🎮 [MATCH_SERVER_READY] Ignoring redirect for terminal match (status=${statusCheck.status}) matchId=${newMatchId}`,
                );
                return;
              }
              armMatchPageLandedSoundForMatch(newMatchId);
              history.push(
                eventAsJson.redirectTo || `/match/${newMatchId}`,
              );
            });
          } else if (eventAsJson.redirectTo) {
            history.push(eventAsJson.redirectTo);
          }
          break;
        }

        case MESSAGE_TYPES.PLATFORM_STATS_UPDATE:
          actions.setPlatformStats({
            serversOnline: eventAsJson.serversOnline,
            playersSearching: eventAsJson.playersSearching,
            activeMatches: eventAsJson.activeMatches,
          });
          break;

        default:
          if (process.env.NODE_ENV !== 'production') console.log("Unhandled message type:", eventAsJson.messageId);
      }
    });

    return unsubscribe;
  }, [user, subscribe, actions, catalog.currentLobby, history]);

  useEffect(() => {
    friendsRef.current = catalog.friends;
  }, [catalog.friends]);

  useEffect(() => {
    matchAcceptDialogStateRef.current = matchAcceptDialogState;
  }, [matchAcceptDialogState]);

  // Play sound notification when match accept dialog opens
  useEffect(() => {
    if (matchAcceptDialogState && matchAcceptDialogState.matchId) {
      if (playedMatchSoundRef.current !== matchAcceptDialogState.matchId) {
        playedMatchSoundRef.current = matchAcceptDialogState.matchId;
        playMatchFoundSound();
      }
    } else if (!matchAcceptDialogState) {
      playedMatchSoundRef.current = null;
      matchAcceptanceCountRef.current.clear();
    }
  }, [matchAcceptDialogState]);

  const { accountStatus = null, bannedUntilDisplayDate = "" } = user || {};
  const lobbyNoticeRefreshKey = [
    user?.phoneNumber || "",
    user?.phoneVerification?.status || "",
    user?.phoneVerification?.verifiedAt || "",
    user?.phoneVerification?.expiresAt || "",
  ].join("|");

  const membershipTierForMaps = catalog?.membership?.tier || TIERS.FREE;
  const enabledLocationCount = Array.isArray(catalog.serverLocations)
    ? catalog.serverLocations
        .map((location) => normalizeLocationId(location?.id ?? location?.awsRegion))
        .filter(Boolean).length
    : 0;

  const onMapClicked = (name) => {
    const decision = canToggleMapSelection({
      selectedMaps: lobbyMapsToDisplay,
      mapName: name,
      tier: membershipTierForMaps,
    });
    if (!decision.allowed) {
      toast.error(decision.message, { durationMs: 5000 });
      return;
    }
    actions.lobbyMapClicked({ name, userId: user.userId, queueMode: lobbyQueueMode });
  };

  const onServerLocationClicked = (locationId) => {
    const decision = canToggleLocationSelection({
      selectedLocations: lobbyServerLocationsToDisplay,
      locationId,
      tier: membershipTierForMaps,
      availableCount: enabledLocationCount,
    });
    if (!decision.allowed) {
      toast.error(decision.message, { durationMs: 5000 });
      return;
    }
    actions.lobbyServerLocationClicked({ locationId, userId: user.userId });
  };

  const toggleAllServerLocationsSelection = (locationIds, allSelected) => {
    if (!allSelected) {
      const decision = canClearAllLocations({
        tier: membershipTierForMaps,
        availableCount: enabledLocationCount,
      });
      if (!decision.allowed) {
        toast.error(decision.message, { durationMs: 5000 });
        return;
      }
    }
    actions.toggleAllServerLocationsSelection({
      locationIds,
      userId: user.userId,
      allSelected,
    });
  };

  const toggleAllMapsSelection = (maps, allSelected) => {
    if (!allSelected) {
      const decision = canClearAllMaps({ tier: membershipTierForMaps });
      if (!decision.allowed) {
        toast.error(decision.message, { durationMs: 5000 });
        return;
      }
    }
    actions.toggleAllMapsSelection({ maps, userId: user.userId, allSelected, queueMode: lobbyQueueMode });
  };

  const handleLeaderMapUpdate = (mapName) => {
    const decision = canToggleMapSelection({
      selectedMaps: lobbyMapsToDisplay,
      mapName,
      tier: membershipTierForMaps,
    });
    if (!decision.allowed) {
      toast.error(decision.message, { durationMs: 5000 });
      return;
    }

    actions.lobbyLeaderMapToggled({
      mapName,
      fallbackMaps: lobbyMapsToDisplay,
    });

    sendOwnerAction({
      messageId: "lobby-map-update",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      mapName,
      clientId,
    });
  };

  const handleLeaderToggleAllMaps = (maps, selectAll) => {
    if (!selectAll) {
      const decision = canClearAllMaps({ tier: membershipTierForMaps });
      if (!decision.allowed) {
        toast.error(decision.message, { durationMs: 5000 });
        return;
      }
    }

    actions.lobbyLeaderMapsToggleAll({ maps, selectAll });

    sendOwnerAction({
      messageId: "lobby-maps-toggle-all",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      maps,
      selectAll,
      clientId,
    });
  };

  const handleLeaderServerLocationUpdate = (locationId) => {
    const decision = canToggleLocationSelection({
      selectedLocations: lobbyServerLocationsToDisplay,
      locationId,
      tier: membershipTierForMaps,
      availableCount: enabledLocationCount,
    });
    if (!decision.allowed) {
      toast.error(decision.message, { durationMs: 5000 });
      return;
    }

    actions.lobbyLeaderServerLocationToggled({
      locationId,
      fallbackLocations: lobbyServerLocationsToDisplay,
    });

    sendOwnerAction({
      messageId: "lobby-server-location-update",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      locationId,
      clientId,
    });
  };

  const handleLeaderToggleAllServerLocations = (locationIds, selectAll) => {
    if (!selectAll) {
      const decision = canClearAllLocations({
        tier: membershipTierForMaps,
        availableCount: enabledLocationCount,
      });
      if (!decision.allowed) {
        toast.error(decision.message, { durationMs: 5000 });
        return;
      }
    }

    const normalizedLocations = (locationIds || [])
      .map((locationId) => normalizeLocationId(locationId))
      .filter(Boolean);
    const nextLocations = selectAll ? normalizedLocations : [];

    actions.lobbyLeaderUpdatedServerLocations({
      lobbyCode: currentLobbyCode,
      lobbyServerLocationsSelected: nextLocations,
    });

    sendOwnerAction({
      messageId: "lobby-server-locations-toggle-all",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      locationIds: normalizedLocations,
      selectAll,
      clientId,
    });
  };

  const handleMatchmakingOptionChange = (settingData) => {
    if (
      settingData.name === "restrictServerLocationsToArea" &&
      settingData.enabled === true
    ) {
      const localLocationIds = (catalog.serverLocations || [])
        .filter((location) => location.isInArea !== false)
        .map((location) => normalizeLocationId(location.id))
        .filter(Boolean);
      actions.setSelectedServerLocations(
        lobbyServerLocationsToDisplay.filter((locationId) =>
          localLocationIds.includes(normalizeLocationId(locationId)),
        ),
      );
    }

    actions.lobbyMatchmakingOptionChanged({
      name: settingData.name,
      enabled: settingData.enabled,
      value: settingData.value,
      hasSlider: settingData.hasSlider,
      listValue: settingData.listValue,
      userId: user.userId,
    });
  };

  const handleLeaderMatchmakingUpdate = (settingData) => {
    if (
      settingData.name === "restrictServerLocationsToArea" &&
      settingData.enabled === true
    ) {
      const localLocationIds = (catalog.serverLocations || [])
        .filter((location) => location.isInArea !== false)
        .map((location) => normalizeLocationId(location.id))
        .filter(Boolean);
      actions.lobbyLeaderUpdatedServerLocations({
        lobbyCode: currentLobbyCode,
        lobbyServerLocationsSelected: lobbyServerLocationsToDisplay.filter(
          (locationId) => localLocationIds.includes(normalizeLocationId(locationId)),
        ),
      });
    }

    actions.lobbyLeaderMatchmakingOptionChanged({
      name: settingData.name,
      enabled: settingData.enabled,
      value: settingData.value,
      hasSlider: settingData.hasSlider,
      listValue: settingData.listValue,
    });

    sendOwnerAction({
      messageId: "lobby-options-update",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      name: settingData.name,
      enabled: settingData.enabled,
      value: settingData.value,
      hasSlider: settingData.hasSlider,
      listValue: settingData.listValue,
      clientId,
    });
  };

  const handleSelectionError = (message) => {
    actions.showQueueErrorMessage(message);
  };

  const getUnconfirmedCustomWorkshopMaps = () => {
    if (!selectedCustomWorkshopMaps.length) return [];
    const confirmedWorkshopIds = readConfirmedCustomMapWorkshopIds();
    return selectedCustomWorkshopMaps.filter(
      (map) => !confirmedWorkshopIds.has(map.workshopItemId),
    );
  };

  const startQueueByMode = async (mode) => {
    const queueMode = lobbyQueueMode;
    if (mode === "solo") {
      await actions.lobbyAddPlayerToQueuePool({ userId: user.userId, queueMode });
      return;
    }

    if (mode === "lobby") {
      const queued = await actions.lobbyAddPlayerToQueuePool({ userId: user.userId, queueMode });
      if (!queued) {
        return;
      }

      if (currentLobbyCode && lobbyMembers.length > 0) {
        sendOwnerAction({
          messageId: "lobby-matchmaking-started",
          lobbyCode: currentLobbyCode,
          leaderUserId: user.userId,
          memberUserIds: lobbyMembers
            .filter((m) => m.userId !== user.userId)
            .map((m) => m.userId),
        });
      }
    }
  };

  const guardQueueStartWithOuroCode = (mode) => {
    if (suppressNextOuroCodePromptRef.current) {
      suppressNextOuroCodePromptRef.current = false;
      guardQueueStartWithCustomMapConfirmation(mode);
      return;
    }

    if (shouldShowOuroCode()) {
      pendingOuroCodeModeRef.current = mode;
      setPendingOuroCodeMode(mode);
      setOuroCodeDialogOpen(true);
      return;
    }
    guardQueueStartWithCustomMapConfirmation(mode);
  };

  const handleAcceptOuroCode = () => {
    if (ouroCodeAcceptLockRef.current) {
      return;
    }
    ouroCodeAcceptLockRef.current = true;

    recordOuroCodeAck();
    suppressNextOuroCodePromptRef.current = true;
    setOuroCodeDialogOpen(false);

    const nextMode = pendingOuroCodeModeRef.current || pendingOuroCodeMode;
    pendingOuroCodeModeRef.current = null;
    setPendingOuroCodeMode(null);

    window.setTimeout(() => {
      ouroCodeAcceptLockRef.current = false;
      if (nextMode) {
        guardQueueStartWithCustomMapConfirmation(nextMode);
      }
    }, 0);
  };

  const handleCloseOuroCode = () => {
    setOuroCodeDialogOpen(false);
    pendingOuroCodeModeRef.current = null;
    setPendingOuroCodeMode(null);
    ouroCodeAcceptLockRef.current = false;
  };

  const guardQueueStartWithCustomMapConfirmation = (mode) => {
    const unconfirmedCustomMaps = getUnconfirmedCustomWorkshopMaps();
    if (!unconfirmedCustomMaps.length) {
      startQueueByMode(mode);
      return;
    }

    setCustomMapSubscriptionDialog({
      open: true,
      maps: unconfirmedCustomMaps,
    });
    setPendingQueueStartMode(mode);
  };

  const handleDismissCustomMapSubscriptionDialog = () => {
    setCustomMapSubscriptionDialog({
      open: false,
      maps: [],
    });
    setPendingQueueStartMode(null);
  };

  const handleConfirmCustomMapSubscription = () => {
    const workshopIdsToConfirm = customMapSubscriptionDialog.maps
      .map((map) => String(map.workshopItemId || "").trim())
      .filter(Boolean);
    if (workshopIdsToConfirm.length > 0) {
      const merged = new Set([
        ...readConfirmedCustomMapWorkshopIds(),
        ...workshopIdsToConfirm,
      ]);
      writeConfirmedCustomMapWorkshopIds([...merged]);
    }

    const nextMode = pendingQueueStartMode;
    setCustomMapSubscriptionDialog({
      open: false,
      maps: [],
    });
    setPendingQueueStartMode(null);

    if (nextMode) {
      startQueueByMode(nextMode);
    }
  };

  let disabledPlayButton = false;

  if (lobbyState === "searching") {
    disabledPlayButton = true;
  }

  // DEBUG: Log button disabled state and reasons
  const isAccountAllowedToPlay = ["active", "verified"].includes(accountStatus);
  if (process.env.NODE_ENV !== 'production') {
    console.log("🎮 PLAY Button Debug:", {
      disabledPlayButton,
      accountStatus,
      isAccountAllowedToPlay,
      isButtonDisabled: disabledPlayButton || !isAccountAllowedToPlay,
      lobbyState,
      currentLobbyCode,
      isLeader,
      mapsCount: lobbyMapsToDisplay?.length || 0,
      user: user
        ? {
            userId: user.userId,
            accountStatus: user.accountStatus,
            email: user.email,
            nickname: user.nickname,
          }
        : "No user",
      renderConditions: {
        lobbyStateStandby: lobbyState === "standby",
        isLeader: isLeader,
        hasLobbyCode: !!currentLobbyCode,
        hasMaps: hasSelectedMaps,
        hasServerLocations: hasSelectedServerLocations,
        willRenderLobbyButton:
          lobbyState === "standby" && isLeader && currentLobbyCode,
        willRenderSoloButton:
          lobbyState === "standby" && !currentLobbyCode,
      },
      reasons: {
        lobbyStateSearching: lobbyState === "searching",
        accountNotAllowedToPlay: !isAccountAllowedToPlay,
        accountStatusValue: accountStatus,
        allowedStatuses: ["active", "verified"],
      },
    });
  }

  const onAcceptMatch = () => {
    actions.lobbyAcceptMatch({ matchId: matchAcceptDialogState.matchId });
    sendOwnerAction({
      messageId: "accept-match",
      matchId: matchAcceptDialogState.matchId,
      userId: user.userId,
      clientId,
    });
    if (process.env.NODE_ENV !== 'production') console.log("accepting match", matchAcceptDialogState.matchId);
  };

  const handleSendRequestFriend = (steamId) => {
    if (notifyIfCannotSendFriendRequest(user)) {
      return;
    }

    if (!resolvedUserSteamId) {
      console.warn(
        "[LobbyPage] Cannot send a friend request without a Steam ID",
        {
          userId: user?.userId || null,
          targetSteamId: steamId || null,
        },
      );
      return;
    }

    actions.addPendingRequestLocally(steamId, resolvedUserSteamId);

    actions.sendFriendRequest({
      recipientSteamId: steamId,
      userId: user.userId,
      userSteamId: resolvedUserSteamId,
    });
  };

  const handleClearFriendRequestMessages = () => {
    actions.clearFriendRequestMessages();
  };

  const handleAcceptFriendRequest = (fromUserSteamId, toUserSteamId) => {
    return actions.acceptFriendRequest({
      fromUserSteamId,
      toUserSteamId,
      toUserId: user.userId,
    });
  };

  const handleFriendRequestToCancel = (friendRequestId) => {
    actions.cancelFriendRequest({ friendRequestId });
  };

  const handleDeclineFriendRequest = (fromUserSteamId, toUserSteamId) => {
    return actions.declineFriendRequest({
      fromUserSteamId,
      toUserSteamId,
      toUserId: user.userId,
    });
  };

  const handleDeleteFriend = (friendSteamId) => {
    const friend = friends.find((f) => f.friendSteamId === friendSteamId);
    actions.openConfirmDialog({ which: "deleteFriend", friend });
  };

  const handleConfirmDeleteFriend = () => {
    if (friendToDelete) {
      if (!resolvedUserSteamId) {
        console.warn("[LobbyPage] Cannot delete a friend without a Steam ID", {
          userId: user?.userId || null,
          friendSteamId: friendToDelete.friendSteamId || null,
        });
        return;
      }

      const friendSteamId = friendToDelete.friendSteamId;
      actions.closeConfirmDialog();
      actions.removeFriendLocally(friendSteamId);
      actions.removePendingRequestLocally(friendSteamId);

      actions.deleteFriend({
        mySteamId: resolvedUserSteamId,
        friendSteamId,
        userId: user.userId,
      });
    }
  };

  const handleCancelConfirmDialog = () => {
    actions.closeConfirmDialog();
  };

  const handleCancelJoinLobbyDialog = () => {
    actions.closeJoinLobbyDialog();
  };

  const handleCloseQueueBlockedModal = () => {
    actions.hideQueueBlockedModal();
  };

  const handleSendInvite = (friend) => {
    if (!resolvedUserSteamId) {
      console.warn(
        "[LobbyPage] Cannot send a lobby invite without a Steam ID",
        {
          userId: user?.userId || null,
          targetUserId: friend?.friendData?.userId || null,
        },
      );
      return;
    }

    if (process.env.NODE_ENV !== 'production') console.log("Sending invite message");
    sendOwnerAction({
      messageId: "invite-send",
      fromUserId: user.userId,
      fromUserSteamId: resolvedUserSteamId,
      toUserId: friend.friendData.userId,
    });

    actions.inviteSent(friend.friendData.userId);
  };

  const handleInviteDeclined = (inviteId, inviteData) => {
    sendOwnerAction({
      messageId: "invite-decline",
      inviteId,
      // fromUserId: inviteData.fromUserId,
      // toUserId: user.userId,
    });
  };

  const handleInviteAccepted = (inviteId, inviteData) => {
    sendOwnerAction({
      messageId: "invite-accept",
      inviteId,
      fromUserId: inviteData.fromUserId,
      toUserId: user.userId,
    });
  };

  const handleClearInviteError = (toUserId) => {
    actions.clearInviteError(toUserId);
  };

  let friendsOnlineCount = 0;
  friends.forEach((friend) => {
    if (friend?.friendData?.isOnline) {
      friendsOnlineCount++;
    }
  });

  const copyTextToClipboard = async (text) => {
    try {
      const normalizedText = String(text || "").trim();
      if (!normalizedText) return false;

      const canTryClipboard =
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function";

      const legacyCopy = () => {
        const el = document.createElement("textarea");
        el.value = normalizedText;
        el.setAttribute("readonly", "");
        el.setAttribute("inputmode", "none");
        el.setAttribute("aria-hidden", "true");
        el.tabIndex = -1;
        el.style.position = "fixed";
        el.style.left = "-9999px";
        el.style.top = "0";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus({ preventScroll: true });
        el.select();
        el.setSelectionRange(0, el.value.length);
        const ok = document.execCommand("copy");
        el.blur();
        document.body.removeChild(el);
        if (
          typeof document !== "undefined" &&
          document.activeElement &&
          typeof document.activeElement.blur === "function"
        ) {
          document.activeElement.blur();
        }
        return ok;
      };

      // Prefer modern clipboard, but fall back if it throws (permissions/gesture quirks).
      if (canTryClipboard) {
        try {
          await navigator.clipboard.writeText(normalizedText);
        } catch (e) {
          if (!legacyCopy()) throw e;
        }
      } else if (!legacyCopy()) {
        throw new Error("Copy failed");
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleCopyLobbyCode = async (code) => {
    const ok = await copyTextToClipboard(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      console.error("Failed to copy lobby code");
    }
  };

  const handleCopyReferralLink = async () => {
    const ok = await copyTextToClipboard(buildReferralInviteLink(referralCode));
    if (ok) {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 1500);
    } else {
      console.error("Failed to copy referral invite link");
    }
  };

  const handleExitLobby = () => {
    actions.closeConfirmDialog();
    actions.leaveLobby();
    sendOwnerAction({
      messageId: "leave-lobby",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
    });
    // Clear invite code state when exiting lobby
    setInviteCode(null);
    setInviteExpiresAt(null);
    toast.info("You left the lobby", { durationMs: 4000 });
  };

  const handleKickPlayerFromLobby = (player) => {
    actions.kickPlayerFromLobby(player.userId);
    sendOwnerAction({
      messageId: "kick-player-from-lobby",
      lobbyCode: currentLobbyCode,
      playerToKickId: player.userId,
      clientId,
    });
    toast.info(`Kicked ${player?.nickname || "player"} from the lobby`, {
      durationMs: 4000,
    });
  };

  const startSoloMatchmaking = () => {
    if (!hasRequiredQueueSelections) {
      actions.showQueueErrorMessage(
        "Select at least one map and one server location before playing.",
      );
      return;
    }
    guardQueueStartWithOuroCode("solo");
  };

  const startLobbyMatchmakingByLeader = () => {
    if (!hasRequiredQueueSelections) {
      actions.showQueueErrorMessage(
        "Select at least one map and one server location before playing.",
      );
      return;
    }
    guardQueueStartWithOuroCode("lobby");
  };

  const handleSmallerMatchOptIn = () => {
    if (user?.userId) {
      actions.lobbySmallerMatchOptIn({ userId: user.userId });
    }
  };

  const handleSmallerMatchDecline = () => {
    if (user?.userId) {
      actions.lobbySmallerMatchDecline({ userId: user.userId });
    }
  };

  const renderSmallerMatchOffer = () => {
    if (!showSmallerMatchOffer) return null;

    const smallerMatchCopy =
      smallerMatchStatus === "opted_in"
        ? getSmallerMatchOptedInCopy({
            teamLabel: smallerMatchLabel,
            optedInCount: smallerMatchOffer.optedInCount,
            matchSize: smallerMatchOffer.matchSize,
          })
        : null;
    const title =
      smallerMatchCopy
        ? smallerMatchCopy.title
        : canActOnSmallerMatch
          ? `Not enough for 5v5 — play ${smallerMatchLabel}?`
          : `Leader is considering a ${smallerMatchLabel}`;
    const hint =
      smallerMatchCopy
        ? smallerMatchCopy.hint
        : "Same skill-based matching, smaller equal teams.";

    return (
      <div className={classes.smallerMatchOfferBox}>
        <Typography className={classes.smallerMatchOfferTitle}>
          {title}
        </Typography>
        <Typography className={classes.smallerMatchOfferHint}>
          {hint}
        </Typography>
        {canActOnSmallerMatch && smallerMatchStatus === "offered" && (
          <div className={classes.smallerMatchOfferActions}>
            <Button
              type="button"
              size="small"
              className={classes.smallerMatchPlayButton}
              onClick={handleSmallerMatchOptIn}
            >
              Play {smallerMatchLabel}
            </Button>
            <Button
              type="button"
              size="small"
              className={classes.smallerMatchDeclineButton}
              onClick={handleSmallerMatchDecline}
            >
              Keep waiting for 5v5
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleStopSearch = () => {
    actions.lobbyStopSearch(user);
    actions.clearQueueStatus();

    if (currentLobbyCode && lobbyMembers.length > 0) {
      sendOwnerAction({
        messageId: "matchmaking-stopped",
        lobbyCode: currentLobbyCode,
        leaderUserId: user.userId,
        memberUserIds: lobbyMembers
          .filter((m) => m.userId !== user.userId)
          .map((m) => m.userId),
      });
    }
  };

  const joinLobbyByCode = (lobbyCode) => {
    sendOwnerAction({
      messageId: "join-lobby-by-code",
      lobbyCode,
      userId: user.userId,
    });
  };

  const handleCreateSoloLobby = () => {
    if (!user?.userId) return;

    sendOwnerAction({
      messageId: "create-solo-lobby",
      userId: user.userId,
    });
  };

  useEffect(() => {
    if (pendingQueueModeRef.current !== "wingman" || !currentLobbyCode) return;
    if (currentLobby?.queueMode === "wingman") {
      pendingQueueModeRef.current = null;
      return;
    }

    pendingQueueModeRef.current = null;
    actions.lobbyQueueModeSwitched({
      lobbyCode: currentLobbyCode,
      queueMode: "wingman",
      maxPlayers: 2,
    });
    sendOwnerAction({
      messageId: "lobby-queue-mode-update",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      queueMode: "wingman",
      clientId,
    });
  }, [currentLobbyCode, currentLobby?.queueMode, user?.userId]);

  const handleLobbyQueueModeSelect = (queueMode) => {
    const maxPlayers = queueMode === "wingman" ? 2 : 5;

    if (!currentLobbyCode) {
      setSoloQueueMode(queueMode);
      pendingQueueModeRef.current = queueMode === "wingman" ? "wingman" : null;
      return;
    }

    actions.lobbyQueueModeSwitched({
      lobbyCode: currentLobbyCode,
      queueMode,
      maxPlayers,
    });

    sendOwnerAction({
      messageId: "lobby-queue-mode-update",
      lobbyCode: currentLobbyCode,
      userId: user.userId,
      queueMode,
      clientId,
    });
  };

  const handleGenerateInviteCode = async () => {
    try {
      if (!basePath) return;
      if (!user?.userId) return;
      if (!currentLobbyCode) return;

      setInviteLoading(true);

      const resp = await fetch(`${basePath}/api/v1/lobby/invite-code`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ttlMinutes: 15 }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.ok !== true) {
        throw new Error(
          data?.message || data?.error || "Failed to generate invite code",
        );
      }

      const nextCode = String(data.inviteCode || "").trim();
      setInviteCode(nextCode || null);
      setInviteExpiresAt(data.expiresAt || null);
      // Do not auto-copy: clipboard fallback focuses a textarea and opens
      // the mobile keyboard when this runs on lobby mount.
    } catch (e) {
      console.error("Failed to generate invite code:", e);
    } finally {
      setInviteLoading(false);
    }
  };

  const formatUtcHourLocal = (utcHour) => {
    if (utcHour == null) return "";
    const parsedUtcHour =
      typeof utcHour === "number"
        ? utcHour
        : Number.parseInt(String(utcHour).split(":")[0], 10);
    if (!Number.isFinite(parsedUtcHour)) return "";
    const d = new Date();
    d.setUTCHours(parsedUtcHour, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const schedule = queueClosedInfo.schedule;
  const localOpenTime = formatUtcHourLocal(schedule?.opensAtUTC);
  const localCloseTime = formatUtcHourLocal(schedule?.closesAtUTC);

  const isScheduleClosed = schedule?.enabled && !schedule?.currentlyOpen;
  const isAdminClosed =
    queueClosedInfo.queueClosed &&
    (!schedule?.enabled || schedule?.currentlyOpen);
  const isScheduleOpenWithTimer = schedule?.enabled && schedule?.currentlyOpen;

  return (
    <div className={classes.root}>
      <div className={classes.homepageRoot}>
        <div className={classes.alertContainer}>
          {isAdminClosed && (
            <Alert severity="info">
              <AlertTitle>Matchmaking queue is temporarily closed</AlertTitle>
              You can’t start searching for a match right now. Please try again
              later.
            </Alert>
          )}

          {isScheduleOpenWithTimer && !queueClosedInfo.queueClosed && (
            <Typography
              sx={{
                color: "rgba(100, 200, 100, 0.9)",
                fontSize: "0.88rem",
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Queue open until {localCloseTime}
            </Typography>
          )}

          <LobbyAccountStatusNotice
            accountStatus={accountStatus}
            phoneVerificationStatus={user?.phoneVerification?.status ?? null}
            readinessRefreshKey={lobbyNoticeRefreshKey}
            user={user}
          />

          <V2DesignPrompt user={user} actions={actions} />

          {accountStatus === "noteligible" && (
            <Alert severity="error">
              You are not eligible to queue for a match on Ouro matchmaking at
              this moment. <br />
              We will email you in case we can re-evaluate your accounts
              standing.
            </Alert>
          )}

          {accountStatus === "banned" && (
            <Alert severity="error">
              You are banned from using Ouro matchmaking until{" "}
              <strong>{bannedUntilDisplayDate}</strong>
            </Alert>
          )}

          {founderProgress?.spotsAvailable &&
            !founderProgress?.userIsFounder &&
            !founderSpotsDismissed && (
              <Alert
                severity="info"
                action={
                  <IconButton
                    aria-label="Dismiss founder spots notice"
                    size="small"
                    onClick={() => {
                      try {
                        localStorage.setItem(FOUNDER_SPOTS_DISMISSED_KEY, "1");
                      } catch {
                        // localStorage unavailable
                      }
                      setFounderSpotsDismissed(true);
                    }}
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      "&:hover": { color: "rgba(255,255,255,0.8)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  backgroundColor: "rgba(192, 160, 80, 0.08)",
                  border: "1px solid rgba(192, 160, 80, 0.2)",
                  mb: 1.5,
                  "& .MuiAlert-icon": { color: "#C0A050" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  <strong>{founderProgress.remaining}</strong> Founder spots
                  remaining —{" "}
                  <Link to="/membership/plans" style={{ color: "#C0A050" }}>
                    Subscribe to claim yours
                  </Link>
                </Typography>
              </Alert>
            )}

          <LobbyMatchNightBanner country={user?.country} />

          {isAccountAllowedToPlay && (
            <BetaWelcomeCard
              discordInviteUrl={discordInviteUrl}
              onInviteFriend={referralCode ? handleCopyReferralLink : null}
              schedule={schedule}
              localOpenTime={localOpenTime}
              localCloseTime={localCloseTime}
              isQueueOpen={isScheduleOpenWithTimer && !queueClosedInfo.queueClosed}
            />
          )}
        </div>

        <Paper
          className={classes.lobbyFrame}
          elevation={0}
          style={{ border: "none" }}
        >
          {!user && <Typography>Please wait...</Typography>}

          {user && (
            <>
              {matchAcceptDialogState && (
                <AcceptMatchDialog
                  accepted={matchAcceptDialogState.accepted}
                  onAcceptMatch={onAcceptMatch}
                  matchId={matchAcceptDialogState.matchId}
                  timeoutSeconds={matchAcceptDialogState.timeoutSeconds}
                  matchAcceptDialogState={matchAcceptDialogState}
                />
              )}

              <InviteDialog
                open={inviteDialogOpen}
                inviteData={inviteData}
                onDecline={handleInviteDeclined}
                onAccept={handleInviteAccepted}
                countdown={inviteCountdown}
              />

              <div className={classes.lobbyContainer}>
                <div
                  className={classNames(
                    classes.cornerBracket,
                    classes.cornerBracketTL,
                  )}
                />
                <div
                  className={classNames(
                    classes.cornerBracket,
                    classes.cornerBracketTR,
                  )}
                />
                <div
                  className={classNames(
                    classes.cornerBracket,
                    classes.cornerBracketBL,
                  )}
                />
                <div
                  className={classNames(
                    classes.cornerBracket,
                    classes.cornerBracketBR,
                  )}
                />


                {!inviteCode && (
                  <div className={classes.containerActionsNoInLobby}>
                    <Tooltip
                      title="Join Lobby with Code"
                      placement="bottom"
                      classes={{ tooltip: classes.friendlistTooltip }}
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 300 }}
                      enterDelay={200}
                      leaveDelay={0}
                    >
                      <IconButton
                        className={classes.lobbyActionIconButton}
                        onClick={() => actions.openJoinLobbyDialog()}
                        disabled={accountStatus === "banned"}
                      >
                        <LoginIcon className={classes.lobbyActionIcon} />
                      </IconButton>
                    </Tooltip>
                    {!currentLobbyData && (
                      <Tooltip
                        title="Create Shareable Lobby"
                        placement="bottom"
                        classes={{ tooltip: classes.friendlistTooltip }}
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 300 }}
                        enterDelay={200}
                        leaveDelay={0}
                      >
                        <IconButton
                          className={classes.lobbyActionIconButton}
                          onClick={handleCreateSoloLobby}
                          disabled={accountStatus === "banned"}
                        >
                          <GroupAddIcon className={classes.lobbyActionIcon} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <div className={classes.containerFriendlistButtonNoInLobby}>
                      <Tooltip
                        title="Friendlist"
                        placement="bottom"
                        classes={{ tooltip: classes.friendlistTooltip }}
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 300 }}
                        enterDelay={200}
                        leaveDelay={0}
                      >
                        <Button
                          variant="contained"
                          className={classes.friendlistButton}
                          onClick={() => actions.setFriendSidebarOpen(true)}
                        >
                          <GroupIcon className={classes.friendlistIcon} />
                          <FiberManualRecordIcon
                            className={`${classes.onlineIndicator} ${
                              friendsOnlineCount > 0
                                ? classes.onlineIndicatorGreen
                                : ""
                            }`}
                          />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {/* Invite Code and Exit Button - Top Right */}
                {currentLobbyCode && (
                  <div className={classes.containerLobbyCodeFriendButton}>
                    <div className={classes.containerLobbyCode}>
                      <div className={classes.lobbyCodeLayout}>
                        <Typography className={classes.lobbyCodeTitle}>
                          Invite Code (60m)
                        </Typography>
                        <div
                          className={classes.lobbyCodeRow}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            handleCopyLobbyCode(inviteCode || currentLobbyCode)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleCopyLobbyCode(
                                inviteCode || currentLobbyCode
                              );
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <Typography className={classes.lobbyCodeText}>
                            {maskInviteCode(inviteCode || currentLobbyCode)}
                          </Typography>
                          <Tooltip
                            title={copied ? "Copied!" : "Copy Full Code"}
                            placement="bottom"
                            arrow
                            classes={{
                              tooltip: classes.copyTooltip,
                              arrow: classes.copyTooltipArrow,
                            }}
                          >
                            <IconButton
                              className={classes.copyButton}
                              onClick={() =>
                                handleCopyLobbyCode(
                                  inviteCode || currentLobbyCode
                                )
                              }
                            >
                              {copied ? (
                                <CheckIcon className={classes.copyIcon} />
                              ) : (
                                <ShareIcon className={classes.copyIcon} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                      <div>
                        <Button
                          variant="contained"
                          className={`${classes.exitLobbyButton} ${
                            shouldBlockActions()
                              ? classes.exitLobbyButtonDisabled
                              : ""
                          }`}
                          onClick={() =>
                            shouldBlockActions()
                              ? actions.showUnavailableActionMessage()
                              : actions.openConfirmDialog({
                                  which: "exitLobby",
                                })
                          }
                        >
                          Exit
                        </Button>
                      </div>
                    </div>
                    <div className={classes.containerFriendlistButton}>
                      <Tooltip
                        title="Friendlist"
                        placement="left"
                        classes={{ tooltip: classes.friendlistTooltip }}
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 300 }}
                        enterDelay={200}
                        leaveDelay={0}
                      >
                        <Button
                          variant="contained"
                          className={classes.friendlistButton}
                          onClick={() => actions.setFriendSidebarOpen(true)}
                        >
                          <GroupIcon className={classes.friendlistIcon} />
                          <FiberManualRecordIcon
                            className={`${classes.onlineIndicator} ${
                              friendsOnlineCount > 0
                                ? classes.onlineIndicatorGreen
                                : ""
                            }`}
                          />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {referralCode && lobbyState === "standby" && (
                  <div className={classes.referralBanner}>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontFamily:
                          "'JetBrains Mono', 'IBM Plex Mono', monospace",
                        fontSize: "0.7rem",
                        letterSpacing: "0.04em",
                      }}
                    >
                      // INVITE FRIENDS · EARN 1 FREE MONTH
                    </Typography>
                    <Chip
                      label={
                        referralCopied
                          ? "LINK COPIED"
                          : "COPY LINK"
                      }
                      size="small"
                      onClick={handleCopyReferralLink}
                      title="Copy your personal invite link"
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "rgba(232, 163, 61, 0.12)",
                        color: "#E8A33D",
                        fontFamily:
                          "'JetBrains Mono', 'IBM Plex Mono', monospace",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        letterSpacing: "0.08em",
                        height: 20,
                        borderRadius: "3px",
                        border: "1px solid rgba(232, 163, 61, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(232, 163, 61, 0.2)",
                        },
                      }}
                    />
                  </div>
                )}

                <div className={classes.lobbySection}>
                  <div className={classes.sectionLabel}>// MATCHMAKING</div>
                  {lobbyState === "standby" && (
                    <LobbyQueueModeSelector
                      queueMode={lobbyQueueMode}
                      isLeader={Boolean(isLeader || !currentLobbyCode)}
                      disabled={shouldBlockActions()}
                      memberCount={lobbyMembers.length || 1}
                      onSelect={handleLobbyQueueModeSelect}
                    />
                  )}
                <div className={classes.queueButtonCont}>
                  {lobbyState === "searching" && (
                    <div className={classes.containerSearchingLabel}>
                      <div className={classes.searchingTimerLabel}>
                        <CircularProgress
                          size={20}
                          thickness={4}
                          style={{ color: "#E8A33D" }}
                          className={classes.searchingMatchLoader}
                        />
                        <Typography className={classes.timerText}>
                          <TimerFromTimestamp
                            timestamp={user.playerQueueActionStartTs}
                          />
                        </Typography>
                      </div>
                      <Typography className={classes.lookformatch}>
                        Searching for a match...
                      </Typography>
                      {renderSmallerMatchOffer()}
                    </div>
                  )}

                  {!isLeader && matchmakingStarted && (
                    <div className={classes.containerSearchingLabel}>
                      <div className={classes.searchingTimerLabel}>
                        <CircularProgress
                          size={20}
                          thickness={4}
                          style={{ color: "#E8A33D" }}
                          className={classes.searchingMatchLoader}
                        />
                        <Typography className={classes.timerText}>
                          Searching...
                        </Typography>
                      </div>
                      <Typography className={classes.lookformatch}>
                        Searching for a match...
                      </Typography>
                      {lobbyState !== "searching" && renderSmallerMatchOffer()}
                    </div>
                  )}

                  {lobbyState === "standby" &&
                  !hasRequiredQueueSelections &&
                  (isLeader || !currentLobbyCode) ? (
                    <Tooltip
                      title={
                        !hasSelectedMaps && !hasSelectedServerLocations
                          ? "Before Play select at least one map and one server location"
                          : !hasSelectedMaps
                            ? "Before Play select at least one map"
                            : "Before Play select at least one server location"
                      }
                      placement="left"
                      classes={{ tooltip: classes.friendlistTooltip }}
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 300 }}
                      enterDelay={200}
                      leaveDelay={0}
                    >
                      <span>
                        <Button
                          size="large"
                          className={classes.playButtonDisabled}
                          disabled
                        >
                          PLAY
                        </Button>
                      </span>
                    </Tooltip>
                  ) : lobbyState === "standby" &&
                    isLeader &&
                    currentLobbyCode ? (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={startLobbyMatchmakingByLeader}
                      disabled={
                        disabledPlayButton ||
                        !hasRequiredQueueSelections ||
                        !isAccountAllowedToPlay ||
                        isQueueEffectivelyClosed
                      }
                      className={classes.playButton}
                    >
                      PLAY
                    </Button>
                  ) : lobbyState === "standby" && !currentLobbyCode ? (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={startSoloMatchmaking}
                      disabled={
                        disabledPlayButton ||
                        !hasRequiredQueueSelections ||
                        !isAccountAllowedToPlay ||
                        isQueueEffectivelyClosed
                      }
                      className={classes.playButton}
                    >
                      PLAY
                    </Button>
                  ) : null}

                  {isAccountAllowedToPlay &&
                    !isQueueEffectivelyClosed &&
                    hasRequiredQueueSelections &&
                    lobbyState === "standby" &&
                    (isLeader || !currentLobbyCode) && (
                      <QueueEstimateIndicator estimate={queueEstimate} />
                    )}

                  {lobbyState === "searching" && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleStopSearch}
                      className={classes.stopSearchButton}
                      color="secondary"
                    >
                      stop
                    </Button>
                  )}

                  {isAccountAllowedToPlay &&
                    !isQueueEffectivelyClosed &&
                    lobbyState === "standby" &&
                    (catalog.platformStats?.playersSearching ?? null) === 0 && (
                      <EmptyQueueHint discordInviteUrl={discordInviteUrl} />
                    )}
                </div>
                </div>

                <div className={classes.lobbySection}>
                  <div className={classes.sectionLabel}>// LINEUP</div>
                  <div
                    className={classes.teamSlotsGrid}
                    style={isWingmanQueue ? {
                      gridTemplateColumns: "repeat(2, minmax(140px, 200px))",
                      justifyContent: "center",
                    } : undefined}
                  >
                    {selfMember ? (
                      <LobbyPlayerBox
                        player={selfMember}
                        isYou={true}
                        lobbyState={lobbyState}
                        currentLobbyCode={currentLobbyCode}
                        canFlipCards={canFlipCards()}
                        showUnavailableActionMessage={
                          actions.showUnavailableActionMessage
                        }
                        avatarCacheBust={catalog?.avatarCacheBust || 0}
                      />
                    ) : (
                      <LobbyPlayerBox
                        player={user}
                        isYou={true}
                        lobbyState={lobbyState}
                        currentLobbyCode={currentLobbyCode}
                        canFlipCards={canFlipCards()}
                        showUnavailableActionMessage={
                          actions.showUnavailableActionMessage
                        }
                        avatarCacheBust={catalog?.avatarCacheBust || 0}
                      />
                    )}
                    {(isWingmanQueue ? [0] : [0, 1, 2, 3]).map((index) => (
                      <FriendSlot
                        key={index}
                        player={otherMembers[index]}
                        onInvite={() => actions.setFriendSidebarOpen(true)}
                        friendsOnline={friendsOnlineCount}
                        pendingRequests={pendingRequests}
                        pendingInvite={pendingBySlot[index] || null}
                        onKick={handleKickPlayerFromLobby}
                        friendlist={friends}
                        handleSendRequestFriend={handleSendRequestFriend}
                        currentLobbyCode={currentLobbyCode}
                        lobbyState={lobbyState}
                        isCurrentUserLeader={selfMember?.role === "host"}
                        canFlipCards={canFlipCards()}
                        showUnavailableActionMessage={
                          actions.showUnavailableActionMessage
                        }
                        avatarCacheBust={catalog?.avatarCacheBust || 0}
                      />
                    ))}
                  </div>
                </div>

                <div className={classes.lobbySection}>
                  <div className={classes.sectionLabel}>// FILTERS</div>
                <div className={classes.bottomBarsContainer}>
                  <div
                    className={classes.settingsRecapContainer}
                    onClick={() =>
                      shouldBlockActions()
                        ? actions.showUnavailableActionMessage()
                        : actions.setLobbySettingsMenuOpen(true)
                    }
                  >
                      {/* Maps Section */}
                      <div className={classes.recapRow}>
                        <MapIcon className={classes.recapIcon} />
                        <MotionFade
                          in={true}
                          durationMs={400}
                          key={lobbyMapsToDisplay.join(",")}
                        >
                          <Typography className={classes.recapText}>
                            {mapsRecapSummary.isEmpty ? (
                              <span className={classes.recapEmptyText}>
                                {mapsRecapSummary.text}
                              </span>
                            ) : (
                              mapsRecapSummary.text
                            )}
                          </Typography>
                        </MotionFade>
                      </div>

                      {/* Server Locations Section */}
                      <div className={classes.recapRow}>
                        <PublicIcon className={classes.recapIcon} />
                        <MotionFade
                          in={true}
                          durationMs={400}
                          key={lobbyServerLocationsToDisplay.join(",")}
                        >
                          <Typography className={classes.recapText}>
                            {serverLocationsRecapSummary.isEmpty ? (
                              <span className={classes.recapEmptyText}>
                                {serverLocationsRecapSummary.text}
                              </span>
                            ) : (
                              serverLocationsRecapSummary.text
                            )}
                          </Typography>
                        </MotionFade>
                      </div>

                      {/* Matchmaking Options Section */}
                      <div className={classes.recapRow}>
                        <TuneIcon className={classes.recapIcon} />
                        <MotionFade
                          in={true}
                          durationMs={400}
                          key={JSON.stringify(lobbyOptionsToDisplay)}
                        >
                          <Typography className={classes.recapText}>
                            {matchmakingRecapSummary.isEmpty ? (
                              <span className={classes.recapEmptyText}>
                                {matchmakingRecapSummary.text}
                              </span>
                            ) : (
                              matchmakingRecapSummary.text
                            )}
                          </Typography>
                        </MotionFade>
                      </div>
                  </div>

                  <SystemStatusBar platformStats={catalog.platformStats} />
                </div>
                </div>
              </div>

              {/* <Chip
                className={classes.inviteLinkBtn}
                avatar={<FileCopyIcon />}
                label="Copy invite link"
                clickable
                color="default"
              /> */}
            </>
          )}
        </Paper>

        {isQueueEffectivelyClosed && (
          <div className={classes.matchmakingStatusBar}>
            <span>// MATCHMAKING WINDOW</span>
            <span className={classes.matchmakingStatusDivider}>·</span>
            <span className={classes.matchmakingStatusTime}>
              OPENS {localOpenTime || "TBD"}
            </span>
            <span className={classes.matchmakingStatusDivider}>·</span>
            <span className={classes.matchmakingStatusTime}>
              {scheduleCountdown || ""}
            </span>
            <span className={classes.matchmakingStatusDivider}>·</span>
            <a
              href={discordInviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.matchmakingStatusLink}
            >
              RALLY ON DISCORD →
            </a>
          </div>
        )}

        <div
          className={classNames(classes.backdrop, {
            [classes.backdropVisible]: catalog.friendSidebarOpen,
          })}
          onClick={() => actions.setFriendSidebarOpen(false)}
        />
        <div
          className={classNames(classes.containerSideBar, {
            [classes.containerSideBarOpen]: catalog.friendSidebarOpen,
          })}
        >
          <FriendListSideBar
            setSidebarOpen={(open) => actions.setFriendSidebarOpen(open)}
            friendRequests={friendRequests}
            handleAcceptFriendRequest={handleAcceptFriendRequest}
            handleDeclineFriendRequest={handleDeclineFriendRequest}
            handleFriendRequestToCancel={handleFriendRequestToCancel}
            friends={friends}
            handleDeleteFriend={handleDeleteFriend}
            handleSendInvite={handleSendInvite}
            handleClearInviteError={handleClearInviteError}
            inviteState={inviteState}
            actions={actions}
            catalog={catalog}
            isLobbyFull={isLobbyFull}
          />
        </div>

        <Dialog
          open={catalog.lobbySettingsMenuOpen}
          onClose={() => actions.setLobbySettingsMenuOpen(false)}
          maxWidth={false}
          PaperProps={{ className: classes.lobbySettingsDialogPaper }}
        >
          <div
            className={classes.lobbySettingsDialogBody}
            data-lenis-prevent
            data-lenis-prevent-wheel
            data-lenis-prevent-touch
          >
            {/* Block Settings Panel */}
            {!canEditLobbySettings && currentLobbyCode && (
              <div className={classes.blockSettingsPanel} />
            )}
            <LobbySettingsMenu
              setMenuSettingsOpen={(open) =>
                actions.setLobbySettingsMenuOpen(open)
              }
              lobbyMapsSelected={lobbyMapsToDisplay}
              selectedServerLocations={lobbyServerLocationsToDisplay}
              currentUserGender={user?.gender || null}
              completedOnboardingTier={user?.completedOnboardingTier || 0}
              onMapClicked={onMapClicked}
              onToggleAllMaps={toggleAllMapsSelection}
              onLeaderMapUpdate={handleLeaderMapUpdate}
              onLeaderToggleAllMaps={handleLeaderToggleAllMaps}
              onServerLocationClicked={onServerLocationClicked}
              onLeaderServerLocationUpdate={handleLeaderServerLocationUpdate}
              onToggleAllServerLocations={toggleAllServerLocationsSelection}
              onLeaderToggleAllServerLocations={
                handleLeaderToggleAllServerLocations
              }
              onMatchmakingOptionChange={handleMatchmakingOptionChange}
              currentLobbyCode={currentLobbyCode}
              canEditLobbySettings={canEditLobbySettings}
              matchmakingOptions={lobbyOptionsToDisplay}
              onLeaderMatchmakingUpdate={handleLeaderMatchmakingUpdate}
              membershipTier={catalog.membership?.tier || "free"}
              serverLocations={catalog.serverLocations}
              onSelectionError={handleSelectionError}
              settingsResetVersion={settingsResetVersion}
              queueEstimate={queueEstimate}
              queueMode={lobbyQueueMode}
            />
          </div>
        </Dialog>

        <JoinLobbyDialog
          open={catalog.joinLobbyDialogOpened}
          onCancel={handleCancelJoinLobbyDialog}
          joinLobbyByCode={joinLobbyByCode}
          lobbyCodeNotFound={lobbyCodeNotFound}
          lobbyFull={lobbyFull}
        />

        <ConfirmDialog
          open={
            catalog.confirmDialog &&
            catalog.confirmDialog.open &&
            catalog.confirmDialog.which === "deleteFriend"
          }
          IconComponent={DeleteIcon}
          title={friendToDelete?.friendData?.nickname || "Friend"}
          message={"Are you sure you want to remove this friend?"}
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleConfirmDeleteFriend}
          onCancel={handleCancelConfirmDialog}
        />

        <ConfirmDialog
          open={
            catalog.confirmDialog &&
            catalog.confirmDialog.open &&
            catalog.confirmDialog.which === "exitLobby"
          }
          IconComponent={ExitToAppIcon}
          title="Exit Lobby"
          message="Are you sure you want to exit the lobby?"
          confirmText="Exit"
          cancelText="Cancel"
          onConfirm={handleExitLobby}
          onCancel={handleCancelConfirmDialog}
        />

        <OuroCodeDialog
          open={ouroCodeDialogOpen}
          onAccept={handleAcceptOuroCode}
          onClose={handleCloseOuroCode}
        />

        <SmallerMatchIntroDialog
          open={smallerMatchIntroOpen}
          onClose={dismissSmallerMatchIntro}
        />

        <Dialog
          open={customMapSubscriptionDialog.open}
          onClose={handleDismissCustomMapSubscriptionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Subscribe to selected custom map(s)</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              Some of your selected maps are Steam Workshop maps. Make sure
              they are subscribed and downloaded locally before you queue.
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              You cannot enter matchmaking with these maps selected until you
              confirm this step.
            </Typography>
            {customMapSubscriptionDialog.maps.map((map) => (
              <Typography key={map.workshopItemId} variant="body2">
                - {map.mapDisplayName}{" "}
                {map.workshopUrl ? (
                  <a
                    href={map.workshopUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#bdbdbd" }}
                  >
                    Open Steam Workshop
                  </a>
                ) : (
                  `(Workshop ID: ${map.workshopItemId})`
                )}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDismissCustomMapSubscriptionDialog}>
              Not now
            </Button>
            <Button
              onClick={handleConfirmCustomMapSubscription}
              variant="contained"
              color="primary"
            >
              I subscribed to these maps
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={queueBlockedModal.open}
          onClose={handleCloseQueueBlockedModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Steam privacy check required</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              {queueBlockedModal.message ||
                "Your Steam profile and CS2 playtime must be public before you can join matchmaking."}
            </Typography>

            {queueBlockedModal.reason === "cs2_hours_not_public" && (
              <>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  To fix this, open Steam privacy settings and make these changes:
                </Typography>
                <Typography component="ol" variant="body2" style={{ marginTop: 0, paddingLeft: 20 }}>
                  <li>Set your Steam profile to Public.</li>
                  <li>Set Game details to Public.</li>
                  <li>Make sure "Always keep my total playtime private" is unchecked.</li>
                </Typography>
                <Typography variant="body2" style={{ marginTop: 12 }}>
                  After saving in Steam, come back and press Play again. We will
                  re-check your CS2 hours before adding you to the queue.
                </Typography>
              </>
            )}

            {Array.isArray(queueBlockedModal.blockedMembers) &&
              queueBlockedModal.blockedMembers.length > 0 && (
                <Typography variant="body2" style={{ marginTop: 12 }}>
                  Blocked player:{" "}
                  {queueBlockedModal.blockedMembers
                    .map((member) => member.nickname || member.userId)
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              )}
          </DialogContent>
          <DialogActions>
            {queueBlockedModal.reason === "cs2_hours_not_public" && (
              <Button
                component="a"
                href="https://steamcommunity.com/my/edit/settings"
                target="_blank"
                rel="noreferrer"
              >
                Open Steam settings
              </Button>
            )}
            <Button
              onClick={handleCloseQueueBlockedModal}
              variant="contained"
              color="primary"
            >
              I understand
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  );
}

LobbyPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(LobbyPage);